import {
  CURRENCY_BY_MARKET,
  CHANNEL_TYPE_LABELS,
  CHARGEBACK_REASON_CODES,
  REDACTED_MARKER,
  SUSPENSION_REASON_CODES,
  type AppealStatus,
  type AuditLogEntry,
  type BalanceLedgerEntry,
  type AffiliateNetwork,
  type AffiliateNetworkMarket,
  type AttributionMethod,
  type AttributionStatus,
  type CannedResponse,
  type ImpersonationSession,
  type CashbackFundingSource,
  type Category,
  type ChannelType,
  type Charity,
  type CharityType,
  type Click,
  type Dataset,
  type DsarRequest,
  type DsarStatus,
  type ConsentRecord,
  type SanctionsMatch,
  type SanctionsList,
  type SanctionsStatus,
  type ErasureRequest,
  type ErasureStatus,
  type ConsentType,
  type FraudStatus,
  type Market,
  type Merchant,
  type MerchantCategory,
  type CmsPage,
  type BlogCategory,
  type BlogPost,
  type Testimonial,
  type SeoRule,
  type SliderSlide,
  type MenuItem,
  type OfflineStore,
  type SystemSetting,
  type FeatureFlag,
  type PlusMarketingFlag,
  type EmailTemplate,
  type ComplianceThreshold,
  type ComplianceStateToggle,
  type ComplianceLegalReference,
  type Event,
  type EventType,
  type AlertConfig,
  type AlertChannelConfig,
  type FiredAlert,
  type MerchantRegion,
  type MerchantRegionTag,
  type PageSchema,
  type PublishedVersion,
  type OwnershipVerificationStatus,
  type PayoutMethod,
  type Placement,
  type PlacementSlot,
  type PlusStatus,
  type PromoCode,
  type RateRule,
  type ReferralRewardStatus,
  type StoreChannel,
  type StoreTag,
  type SuspensionReasonCode,
  type Ticket,
  type TrackingMethod,
  type Transaction,
  type CashoutRequest,
  type WelcomeBonusFunding,
  type User,
} from './types';
import type { Block } from '../pageSchema/blocks';

/**
 * Deterministic mock dataset for the consolidated target schema.
 *
 * Generated from a fixed seed so the data is identical on every run (stable
 * tests, stable demos). It models the normalised backbone (merchants ->
 * merchant_regions -> store_channels, transactions, cashout_requests) plus the
 * re-incorporated first-class entities (affiliate networks, charities, store
 * tags, hierarchical categories) with full referential consistency:
 *
 *  - every merchant has >= 1 merchant_region; every region's channels/tags/txns
 *    belong to it; tag join rows mirror the denormalised tagKeys.
 *  - a transaction's user, merchant_region and (optional) click all share one
 *    market; currency is derived from that market (D2).
 *  - a payout cashout uses a market-correct rail; a donation cashout points at a
 *    real charity in the same market; at least one donation is settled ("donated").
 *  - derived fields (user balance, network storeCount, merchant/region clicks)
 *    are NOT stored here — the provider computes them.
 *
 * The seed populates uk/us only; the types reserve `ie` for a later market.
 *
 * Internal to the data layer — surfaces never import it. They talk to the
 * DataProvider (see ./index.ts).
 */

// --- deterministic PRNG -----------------------------------------------------
const SEED = 0x5c45b1ac;
const mulberry32 = (seed: number) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

type Rng = () => number;
const pick = <T>(rng: Rng, arr: readonly T[]): T =>
  arr[Math.floor(rng() * arr.length)];
const randInt = (rng: Rng, min: number, max: number): number =>
  min + Math.floor(rng() * (max - min + 1));
const round2 = (n: number): number => Math.round(n * 100) / 100;
// Rates carry 4-decimal precision to match the backbone's numeric(6,4).
const round4 = (n: number): number => Math.round(n * 10000) / 10000;
const chance = (rng: Rng, p: number): boolean => rng() < p;
const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

// Fixed base date so generated timestamps are deterministic (no Date.now()).
const BASE_MS = Date.UTC(2025, 5, 1); // 2025-06-01
const DAY = 86_400_000;
const HOUR = 3_600_000;
const daysAgo = (days: number): string =>
  new Date(BASE_MS - days * DAY).toISOString();
const daysAhead = (days: number): string =>
  new Date(BASE_MS + days * DAY).toISOString();

const marketFor = (rng: Rng): Market => (chance(rng, 0.6) ? 'uk' : 'us');

// --- pools ------------------------------------------------------------------
const BRAND_NAMES = [
  'ASOS',
  'Boots',
  'Argos',
  'Currys',
  'Nike',
  'Adidas',
  'JD Sports',
  'Marks & Spencer',
  'Tesco',
  'Sainsburys',
  'Superdrug',
  'The Body Shop',
  'Lookfantastic',
  'Etsy',
  'eBay',
  'Booking.com',
  'Expedia',
  'PrettyLittleThing',
  'Pretty Little Africa',
  'House of CB',
  'Afro Hair & Beauty',
  'Cantu',
  'Shea Moisture',
  'Black Owned Market',
  'Spice Kitchen',
  'Wakuda',
  'Lemonade Dolls',
  'Target',
  'Walmart',
  'Best Buy',
  'Macys',
  'Nordstrom',
  'Sephora',
  'Ulta Beauty',
  'Wayfair',
  'Chewy',
  'The Folklore',
  'Brother Vellies',
  'Telfar',
  'Mented Cosmetics',
  'BLK & Bold',
  'Partake Foods',
] as const;

const FIRST_NAMES = [
  'Ada',
  'Kwame',
  'Amara',
  'Tunde',
  'Zara',
  'Marcus',
  'Nia',
  'Femi',
  'Imani',
  'Jordan',
  'Olu',
  'Chloe',
  'Dwayne',
  'Aisha',
  'Leon',
  'Maya',
  'Kojo',
  'Simone',
  'Andre',
  'Yaa',
  'Ben',
  'Grace',
  'Tobi',
  'Renee',
] as const;
const LAST_NAMES = [
  'Okafor',
  'Mensah',
  'Williams',
  'Adeyemi',
  'Johnson',
  'Boateng',
  'Campbell',
  'Owusu',
  'Brown',
  'Asante',
  'Clarke',
  'Diallo',
  'Okoro',
  'Bennett',
  'Mwangi',
  'Carter',
  'Abara',
  'Lewis',
  'Nkemdirim',
  'Reid',
] as const;

// Ownership / founder data (D-ownership). Realistic-but-fake heritage countries
// for Black-owned brands; the founder names reuse the name pools above.
const HERITAGE_COUNTRIES = [
  'Nigeria',
  'Ghana',
  'Jamaica',
  'United States',
  'United Kingdom',
  'Kenya',
  'South Africa',
  'Trinidad and Tobago',
  'Ethiopia',
  'Senegal',
  'Barbados',
  'Ivory Coast',
  'Cameroon',
  'Somalia',
  'Sierra Leone',
  'Rwanda',
] as const;

// Weighted toward verified (most listed brands have cleared vetting) but with a
// realistic mix of pending and not-yet-verified.
const OWNERSHIP_STATUSES: readonly OwnershipVerificationStatus[] = [
  'verified',
  'verified',
  'verified',
  'verified',
  'pending',
  'pending',
  'unverified',
];

// Affiliate networks (config). storeCount is DERIVED by the provider, so it is
// omitted here (it is an optional field on the type). apiCredential is FIXED
// content (a placeholder network-account secret, D-settings-s2-networks): no draw
// from the main rng, so the main seed stream stays byte-identical. It is redacted
// in the audit (SENSITIVE_AUDIT_FIELDS) and write-only in the edit form.
const NETWORKS_SEED: readonly AffiliateNetwork[] = [
  {
    id: 1,
    name: 'Awin',
    slug: 'awin',
    clickParam: 'awc',
    deeplinkParam: 'ued',
    trackingDomain: 'awin1.com',
    lastImportedAt: daysAgo(2),
    apiCredential: 'seed-cred-awin-01',
  },
  {
    id: 2,
    name: 'Impact',
    slug: 'impact',
    clickParam: 'irclickid',
    deeplinkParam: 'u',
    trackingDomain: 'sjv.io',
    lastImportedAt: daysAgo(1),
    apiCredential: 'seed-cred-impact-01',
  },
  {
    id: 3,
    name: 'CJ Affiliate',
    slug: 'cj-affiliate',
    clickParam: 'cjevent',
    deeplinkParam: 'url',
    trackingDomain: 'anrdoezrs.net',
    lastImportedAt: daysAgo(3),
    apiCredential: 'seed-cred-cj-01',
  },
  {
    id: 4,
    name: 'Partnerize',
    slug: 'partnerize',
    clickParam: 'clickref',
    deeplinkParam: 'destination',
    trackingDomain: 'prf.hn',
    lastImportedAt: daysAgo(6),
    apiCredential: 'seed-cred-partnerize-01',
  },
  {
    id: 5,
    name: 'Afrofiliate',
    slug: 'afrofiliate',
    clickParam: 'afid',
    deeplinkParam: 'url',
    trackingDomain: 'afrofiliate.com',
    lastImportedAt: daysAgo(5),
    apiCredential: 'seed-cred-afrofiliate-01',
  },
  {
    id: 6,
    name: 'Webgains',
    slug: 'webgains',
    clickParam: 'wgcampaignid',
    deeplinkParam: 'wgtarget',
    trackingDomain: 'webgains.io',
    lastImportedAt: daysAgo(7),
    apiCredential: 'seed-cred-webgains-01',
  },
  {
    id: 7,
    name: 'Tradedoubler',
    slug: 'tradedoubler',
    clickParam: 'tduid',
    deeplinkParam: 'url',
    trackingDomain: 'tradedoubler.com',
    lastImportedAt: daysAgo(8),
    apiCredential: 'seed-cred-tradedoubler-01',
  },
  {
    id: 8,
    name: 'Local Stores Ecommerce',
    slug: 'local-stores-ecommerce',
    clickParam: 'lse',
    deeplinkParam: 'to',
    trackingDomain: 'localstores.example',
    lastImportedAt: daysAgo(9),
    apiCredential: 'seed-cred-localstores-01',
  },
];

// Afrofiliate is the sole tracking platform going forward (D-afrofiliate), so
// it is the network on every merchant_region unless a brand is explicitly listed
// here as legacy on another network. These are the only brands NOT on Afrofiliate;
// the other networks stay in NETWORKS_SEED as legacy and reference entries only.
const LEGACY_NETWORK_BY_MERCHANT: Record<string, string> = {
  asos: 'awin',
  argos: 'awin',
  currys: 'cj-affiliate',
  'booking-com': 'impact',
  expedia: 'partnerize',
  ebay: 'webgains',
};

// Placement / scoping tags shown as chips in the old admin.
const STORE_TAGS_SEED: readonly StoreTag[] = [
  {
    id: 1,
    key: 'featured_sidebar',
    label: 'Featured (sidebar)',
    description: 'Pinned in the web sidebar.',
  },
  {
    id: 2,
    key: 'app_featured1_homepage',
    label: 'App featured (homepage)',
    description: 'Featured row on the app homepage.',
  },
  {
    id: 3,
    key: 'afrobot_homepage',
    label: 'Afrobot homepage',
    description: 'Surfaced by the Afrobot homepage.',
  },
  {
    id: 4,
    key: 'top_stores',
    label: 'Top stores',
    description: 'Top stores carousel.',
  },
  {
    id: 5,
    key: 'top_stores_in_afrobot',
    label: 'Top stores (Afrobot)',
    description: 'Top stores within Afrobot.',
  },
];

// Charity-type taxonomy — a LOOKUP TABLE (user-managed in the old admin), not an enum.
const CHARITY_TYPES_SEED: readonly CharityType[] = [
  { id: 1, key: 'health', label: 'Health & Medical', description: '' },
  { id: 2, key: 'education', label: 'Education', description: '' },
  { id: 3, key: 'poverty_relief', label: 'Poverty Relief', description: '' },
  { id: 4, key: 'community', label: 'Community', description: '' },
  { id: 5, key: 'environment', label: 'Environment', description: '' },
  { id: 6, key: 'humanitarian', label: 'Humanitarian', description: '' },
];

const CHARITY_NAMES: Record<Market, readonly string[]> = {
  uk: [
    'Black Cultural Archives',
    'Sickle Cell Society',
    'Stephen Lawrence Day Foundation',
    'The Ubele Initiative',
    'Trussell Trust',
    'Macmillan Cancer Support',
  ],
  us: [
    'UNCF',
    'NAACP Foundation',
    'Thurgood Marshall College Fund',
    'Feeding America',
    'National Urban League',
    'Color Of Change',
  ],
  ie: [],
};

// The marketplace / in-store channel types a brand MAY also carry beyond its
// direct channel. The storefront marketplaces (etsy / amazon / ebay / doordash)
// are seeded by an independent post-pass below, so the main stream's pick set is
// unchanged. Labels come from the shared CHANNEL_TYPE_LABELS map (./types).
const CHANNEL_TYPES = [
  'deliveroo',
  'uber_eats',
  'just_eat',
  'brick_and_mortar',
] as const;

// The storefront marketplaces appended by the independent post-pass (the
// channel-enum extension; D-multi-channel-editor). Kept off the main pick set so
// the seed stream stays byte-identical.
const STOREFRONT_CHANNEL_TYPES: readonly ChannelType[] = [
  'etsy',
  'amazon',
  'ebay',
  'doordash',
];

// Merchant-level tracking / attribution (weighted toward the common cases).
const TRACKING_METHODS: readonly TrackingMethod[] = [
  'affiliate',
  'affiliate',
  'affiliate',
  'clo',
  'ob',
  'multi_rail',
  'none',
];
const ATTRIBUTION_METHODS: readonly AttributionMethod[] = [
  'auto_direct',
  'auto_direct',
  'auto_subnetwork',
  'manual',
  'pending_integration',
];
// Funding source follows the tracking method (clo/ob are merchant-direct).
const fundingFor = (tm: TrackingMethod): CashbackFundingSource =>
  tm === 'clo'
    ? 'merchant_direct_clo'
    : tm === 'ob'
      ? 'merchant_direct_ob'
      : 'affiliate_commission';

const TX_STATUSES: Transaction['status'][] = [
  'pending',
  'confirmed',
  'confirmed',
  'confirmed',
  'waiting',
  'rejected',
  'cancelled',
];

const payoutMethodFor = (rng: Rng, market: Market): PayoutMethod => {
  const rails: Record<Market, readonly PayoutMethod[]> = {
    uk: ['uk_bank', 'uk_paypal'],
    us: ['us_ach', 'us_paypal', 'us_venmo'],
    ie: ['uk_paypal'],
  };
  return pick(rng, rails[market]);
};

// A handful of plausible historical audit entries so the Audit list renders
// meaningfully before any real mutation happens (D-audit). Clearly-fake but
// consistent actors. The before/after snapshots already obey the audit rules:
// only the CHANGED fields appear, and the sensitive `payoutDetails` is recorded
// by name with the redacted marker (REDACTED_MARKER), never by value — exactly
// what the runtime wrapper produces.
const SEED_ADMIN = {
  id: 'seed-admin-1',
  email: 'amara.admin@cashblack.example',
  role: 'super_admin',
} as const;
const SEED_EDITOR = {
  id: 'seed-editor-1',
  email: 'tunde.editor@cashblack.example',
  role: 'editor',
} as const;
// Two more actors so the investigative actor / role facets visibly narrow
// (D-audit-view): a second editor (distinct email, same role, so actor and role
// are clearly different filters) and a fraud reviewer (a distinct role).
const SEED_EDITOR_2 = {
  id: 'seed-editor-2',
  email: 'nia.editor@cashblack.example',
  role: 'editor',
} as const;
const SEED_FRAUD = {
  id: 'seed-fraud-1',
  email: 'kwame.fraud@cashblack.example',
  role: 'fraud',
} as const;
// A compliance officer actor (D-audit-compliance-read): the audit-query gate expansion lets
// compliance read the trail, so the seed carries a compliance-authored entry to prove the
// oversight read sees its OWN role's actions as well as super_admin's (full parity, no subset
// hidden). A DSAR fulfilment is a natural compliance mutation.
const SEED_COMPLIANCE = {
  id: 'seed-compliance-1',
  email: 'zora.compliance@cashblack.example',
  role: 'compliance',
} as const;

const SEED_AUDIT_LOG: readonly AuditLogEntry[] = [
  {
    id: 1,
    actorId: SEED_ADMIN.id,
    actorEmail: SEED_ADMIN.email,
    actorRole: SEED_ADMIN.role,
    action: 'update',
    resource: 'merchants',
    recordId: 5,
    before: { status: 'active' },
    after: { status: 'paused' },
    region: 'UK',
    occurredAt: daysAgo(9),
  },
  {
    id: 2,
    actorId: SEED_ADMIN.id,
    actorEmail: SEED_ADMIN.email,
    actorRole: SEED_ADMIN.role,
    action: 'update',
    resource: 'merchantRegions',
    recordId: 12,
    before: { baseRate: 4.2 },
    after: { baseRate: 5 },
    region: 'UK',
    occurredAt: daysAgo(7),
  },
  {
    id: 3,
    actorId: SEED_EDITOR.id,
    actorEmail: SEED_EDITOR.email,
    actorRole: SEED_EDITOR.role,
    action: 'create',
    resource: 'charities',
    recordId: 7,
    before: null,
    after: { name: 'Color Of Change', market: 'us', status: 'active' },
    region: 'US',
    occurredAt: daysAgo(5),
  },
  {
    // Multi-field edit touching the opaque payoutDetails: the change is recorded,
    // the value is NOT — proof the redaction rule is in the data, not just live.
    id: 4,
    actorId: SEED_ADMIN.id,
    actorEmail: SEED_ADMIN.email,
    actorRole: SEED_ADMIN.role,
    action: 'update',
    resource: 'cashoutRequests',
    recordId: 3,
    before: { status: 'requested', payoutDetails: REDACTED_MARKER },
    after: { status: 'processing', payoutDetails: REDACTED_MARKER },
    region: 'UK',
    occurredAt: daysAgo(3),
  },
  {
    id: 5,
    actorId: SEED_ADMIN.id,
    actorEmail: SEED_ADMIN.email,
    actorRole: SEED_ADMIN.role,
    action: 'delete',
    resource: 'storeTags',
    recordId: 5,
    before: { key: 'top_stores_in_afrobot', label: 'Top stores (Afrobot)' },
    after: null,
    region: 'UK',
    occurredAt: daysAgo(2),
  },
  {
    id: 6,
    actorId: SEED_EDITOR.id,
    actorEmail: SEED_EDITOR.email,
    actorRole: SEED_EDITOR.role,
    action: 'update',
    resource: 'users',
    recordId: 10,
    before: { tier: 'free' },
    after: { tier: 'plus' },
    region: 'US',
    occurredAt: daysAgo(1),
  },
  // --- investigative history extension (D-audit-view) -----------------------
  // More actors, a wide date spread, and entries touching the NEWER resources
  // (promoCodes, placements, users writes from referrals/subscriptions, the
  // attribution writes on transactions) so each new facet has something to filter.
  // Status transitions are all action 'update' (Q1): they are told apart by the
  // resource plus the changed field in the diff, not by a finer action type.
  {
    id: 7,
    actorId: SEED_ADMIN.id,
    actorEmail: SEED_ADMIN.email,
    actorRole: SEED_ADMIN.role,
    action: 'update',
    resource: 'promoCodes',
    recordId: 3,
    before: { active: true },
    after: { active: false },
    region: 'UK',
    occurredAt: daysAgo(15),
  },
  {
    id: 8,
    actorId: SEED_EDITOR.id,
    actorEmail: SEED_EDITOR.email,
    actorRole: SEED_EDITOR.role,
    action: 'create',
    resource: 'promoCodes',
    recordId: 9,
    before: null,
    after: { code: 'UKBOOST10', market: 'uk', active: true },
    region: 'UK',
    occurredAt: daysAgo(20),
  },
  {
    id: 9,
    actorId: SEED_EDITOR_2.id,
    actorEmail: SEED_EDITOR_2.email,
    actorRole: SEED_EDITOR_2.role,
    action: 'create',
    resource: 'placements',
    recordId: 4,
    before: null,
    after: { slot: 'homepage_hero', market: 'us', weight: 3 },
    region: 'US',
    occurredAt: daysAgo(30),
  },
  {
    // A placement "End" is a window write, recorded as a plain update (Q1).
    id: 10,
    actorId: SEED_EDITOR_2.id,
    actorEmail: SEED_EDITOR_2.email,
    actorRole: SEED_EDITOR_2.role,
    action: 'update',
    resource: 'placements',
    recordId: 4,
    before: { endsAt: null },
    after: { endsAt: daysAgo(12) },
    region: 'US',
    occurredAt: daysAgo(12),
  },
  {
    // A referral void is a users write (Q1): resource + the changed field identify it.
    id: 11,
    actorId: SEED_EDITOR.id,
    actorEmail: SEED_EDITOR.email,
    actorRole: SEED_EDITOR.role,
    action: 'update',
    resource: 'users',
    recordId: 13,
    before: { referralRewardStatus: 'pending' },
    after: { referralRewardStatus: 'void' },
    region: 'US',
    occurredAt: daysAgo(45),
  },
  {
    // A Plus comp is a users write.
    id: 12,
    actorId: SEED_ADMIN.id,
    actorEmail: SEED_ADMIN.email,
    actorRole: SEED_ADMIN.role,
    action: 'update',
    resource: 'users',
    recordId: 10,
    before: { plusStatus: 'lapsed' },
    after: { plusStatus: 'active' },
    region: 'UK',
    occurredAt: daysAgo(60),
  },
  {
    // A manual attribution CREDIT is a transactions write: userId null -> set.
    id: 13,
    actorId: SEED_ADMIN.id,
    actorEmail: SEED_ADMIN.email,
    actorRole: SEED_ADMIN.role,
    action: 'update',
    resource: 'transactions',
    recordId: 131,
    before: { userId: null, attributionStatus: 'unattributed' },
    after: { userId: 100, attributionStatus: 'attributed', matchedClickId: 1 },
    region: 'UK',
    occurredAt: daysAgo(8),
  },
  {
    // A manual attribution REJECT, by the fraud reviewer.
    id: 14,
    actorId: SEED_FRAUD.id,
    actorEmail: SEED_FRAUD.email,
    actorRole: SEED_FRAUD.role,
    action: 'update',
    resource: 'transactions',
    recordId: 132,
    before: { attributionStatus: 'unattributed' },
    after: {
      attributionStatus: 'rejected',
      attributionNote: 'no member match',
    },
    region: 'US',
    occurredAt: daysAgo(90),
  },
  {
    // A cashout approve taken while in GLOBAL scope: region context GLOBAL.
    id: 15,
    actorId: SEED_ADMIN.id,
    actorEmail: SEED_ADMIN.email,
    actorRole: SEED_ADMIN.role,
    action: 'update',
    resource: 'cashoutRequests',
    recordId: 7,
    before: { status: 'requested' },
    after: { status: 'approved' },
    region: 'GLOBAL',
    occurredAt: daysAgo(120),
  },
  {
    // A second redacted entry, far in the past, by the fraud reviewer in GLOBAL
    // scope: the redaction rule holds across actors and time.
    id: 16,
    actorId: SEED_FRAUD.id,
    actorEmail: SEED_FRAUD.email,
    actorRole: SEED_FRAUD.role,
    action: 'update',
    resource: 'cashoutRequests',
    recordId: 9,
    before: { status: 'requested', payoutDetails: REDACTED_MARKER },
    after: { status: 'rejected', payoutDetails: REDACTED_MARKER },
    region: 'GLOBAL',
    occurredAt: daysAgo(150),
  },
  {
    // A DSAR fulfilment by the compliance officer (D-audit-compliance-read): a compliance-authored
    // entry, so the widened oversight read sees its own role's actions, not just super_admin's.
    // The trail is oversight-global, so it carries the entry regardless of the reader's region.
    id: 17,
    actorId: SEED_COMPLIANCE.id,
    actorEmail: SEED_COMPLIANCE.email,
    actorRole: SEED_COMPLIANCE.role,
    action: 'update',
    resource: 'dsarRequests',
    recordId: 2,
    before: { status: 'in_progress' },
    after: { status: 'fulfilled' },
    region: 'GLOBAL',
    occurredAt: daysAgo(4),
  },
];

// --- generation -------------------------------------------------------------
export function createSeedData(): Dataset {
  const rng = mulberry32(SEED);
  // Separate, independent PRNG for ownership/founder data so adding it does not
  // perturb the main stream (every other seeded value stays byte-identical).
  const ownRng = mulberry32(SEED ^ 0x1b873593);
  // Likewise for the transaction lifecycle timestamps (occurredAt / confirmedAt
  // / expectedConfirmationAt) — drawn from this independent stream so the main
  // stream is unperturbed.
  const txRng = mulberry32(SEED ^ 0x2545f491);
  // And again for the legacy-user marker (isLegacy / legacySignupAt /
  // legacyUserId) — its own independent stream so adding it leaves every other
  // seeded value byte-for-byte unchanged (D-legacy-users).
  const legacyRng = mulberry32(SEED ^ 0x9e3779b1);
  // And again for the Plus subscription fields (plusStatus / currentPeriodEnd /
  // autoRenew / billingReference) — its own independent stream so the main seed
  // stays byte-identical (D-subscriptions).
  const subRng = mulberry32(SEED ^ 0xc2b2ae35);
  // And again for the promo-codes catalogue (D-promo-codes) — its own independent
  // stream, so adding a whole new dataset leaves the main stream byte-identical.
  const promoRng = mulberry32(SEED ^ 0x27d4eb2f);
  // And again for the referral fields (referredBy / referralRewardStatus /
  // referredAt) on the user row (D-referrals) — its own independent stream.
  const refRng = mulberry32(SEED ^ 0x165667b1);
  // And again for the placements catalogue (D-placements) — its own independent
  // stream, so adding a whole new dataset leaves the main stream byte-identical.
  const placeRng = mulberry32(SEED ^ 0x3c6ef35f);
  // And again for the manual-attribution orphans (D-manual-attribution) — its own
  // independent stream, so adding orphan transactions leaves the main stream
  // byte-identical (normal transactions are unchanged but for a constant
  // attributionStatus, which draws no randomness).
  const attrRng = mulberry32(SEED ^ 0xff51afd7);
  // And again for the fraud fields (riskScore / fraudStatus / suspensionEndsAt /
  // fraudReason / escalated) on the user row (D-fraud-queue) — its own independent
  // stream, so lifting the risk-field hold leaves the main seed byte-identical.
  const fraudRng = mulberry32(SEED ^ 0xd6e8feb8);
  // And again for the suspension-flow fields (suspensionReasonCode / appealStatus /
  // appealedAt / appealNote) on the user row (D-suspension-flow) — its OWN stream,
  // separate from fraudRng too, so layering the lifecycle on leaves BOTH the main
  // stream and the existing fraud values byte-identical.
  const suspRng = mulberry32(SEED ^ 0xa54ff53a);
  // And again for the chargeback / dispute lifecycle (D-chargebacks) layered onto
  // confirmed transactions, plus the reversal ledger entries - its OWN stream, so
  // adding the dispute concept leaves the main stream byte-identical.
  const cbRng = mulberry32(SEED ^ 0x6a09e667);
  // And again for the manual balance adjustments (D-balance-adjustment, the Phase 5
  // capstone) appended to the SAME shared ledger - its OWN stream, drawn AFTER the
  // chargeback pass so the reversal entries stay byte-identical and the main stream is
  // untouched. A handful of members carry an admin-recorded credit or debit.
  const adjRng = mulberry32(SEED ^ 0xbb67ae85);

  // Affiliate networks + per-market commission defaults.
  const affiliateNetworks: AffiliateNetwork[] = NETWORKS_SEED.map((n) => ({
    ...n,
  }));
  const affiliateNetworkMarkets: AffiliateNetworkMarket[] = [];
  let anmId = 0;
  for (const net of affiliateNetworks) {
    for (const market of ['uk', 'us'] as const) {
      affiliateNetworkMarkets.push({
        id: ++anmId,
        networkId: net.id,
        market,
        defaultCommissionRate: round4(randInt(rng, 3, 12) + rng()),
      });
    }
  }

  // Store tags.
  const storeTags: StoreTag[] = STORE_TAGS_SEED.map((t) => ({ ...t }));

  // Charity types + charities (region-scoped).
  const charityTypes: CharityType[] = CHARITY_TYPES_SEED.map((t) => ({ ...t }));
  const charities: Charity[] = [];
  let charityId = 0;
  for (const market of ['uk', 'us'] as const) {
    for (const name of CHARITY_NAMES[market]) {
      const type = pick(rng, charityTypes);
      charities.push({
        id: ++charityId,
        name,
        charityTypeId: type.id,
        description: `${name} — partnered ${market.toUpperCase()} charity.`,
        logoUrl: `https://logo.example/${slugify(name)}.png`,
        bannerUrl: `https://banner.example/${slugify(name)}.jpg`,
        market,
        status: chance(rng, 0.9) ? 'active' : 'inactive',
        createdAt: daysAgo(randInt(rng, 30, 800)),
      });
    }
  }
  const charitiesByMarket: Record<Market, Charity[]> = {
    uk: charities.filter((c) => c.market === 'uk'),
    us: charities.filter((c) => c.market === 'us'),
    ie: [],
  };

  // Categories — hierarchical, shared across markets (D3). Tops cover all 7
  // coarse enum values; children inherit their parent's coarse value.
  const categories: Category[] = [];
  const TOPS: ReadonlyArray<{
    name: string;
    coarse: MerchantCategory;
    children: readonly string[];
  }> = [
    {
      name: 'Fashion',
      coarse: 'fashion',
      children: ['Womenswear', 'Menswear', 'Footwear', 'Accessories'],
    },
    {
      name: 'Beauty & Hair',
      coarse: 'hair_and_beauty',
      children: ['Afro Hair', 'Skincare', 'Makeup', 'Fragrance'],
    },
    {
      name: 'Travel',
      coarse: 'travel',
      children: ['Flights', 'Hotels', 'Experiences'],
    },
    {
      name: 'Food & Drink',
      coarse: 'food',
      children: ['Groceries', 'Takeaway', 'Drinks'],
    },
    {
      name: 'Finance',
      coarse: 'finance',
      children: ['Banking', 'Investing', 'Insurance'],
    },
    {
      name: 'Remittance',
      coarse: 'remittance',
      children: ['Money Transfer'],
    },
    {
      name: 'Electronics & Home',
      coarse: 'other',
      children: ['Phones', 'Computing', 'Audio', 'Furniture'],
    },
  ];
  let catId = 0;
  let order = 0;
  const leafCategories: Category[] = [];
  for (const top of TOPS) {
    const parent: Category = {
      id: ++catId,
      name: top.name,
      slug: slugify(top.name),
      parentId: null,
      coarseCategory: top.coarse,
      displayOrder: order++,
      status: 'active',
      createdAt: daysAgo(900),
    };
    categories.push(parent);
    for (const child of top.children) {
      const leaf: Category = {
        id: ++catId,
        name: child,
        slug: slugify(child),
        parentId: parent.id,
        coarseCategory: top.coarse,
        displayOrder: order++,
        status: 'active',
        createdAt: daysAgo(900),
      };
      categories.push(leaf);
      leafCategories.push(leaf);
    }
  }

  // Merchants + their regions + channels + tag joins.
  const merchants: Merchant[] = [];
  const merchantRegions: MerchantRegion[] = [];
  const storeChannels: StoreChannel[] = [];
  const merchantRegionTags: MerchantRegionTag[] = [];
  let regionId = 0;
  let channelId = 0;
  let mrTagId = 0;

  BRAND_NAMES.forEach((name, i) => {
    const category = pick(rng, leafCategories);
    // Tracking / funding / attribution live on the merchant (backbone homes).
    const trackingMethod = pick(rng, TRACKING_METHODS);
    const merchant: Merchant = {
      id: i + 1,
      name,
      slug: slugify(name),
      description: `${name} — cashback partner.`,
      terms: 'Cashback excludes gift cards and may be declined on returns.',
      categoryId: category.id,
      coarseCategory: category.coarseCategory,
      logoUrl: `https://logo.example/${slugify(name)}.png`,
      status: chance(rng, 0.85)
        ? 'active'
        : chance(rng, 0.6)
          ? 'paused'
          : 'archived',
      trackingMethod,
      cashbackFundingSource: fundingFor(trackingMethod),
      attributionMethod: pick(rng, ATTRIBUTION_METHODS),
      // Ownership / founder / verification (D-ownership) — drawn from `ownRng`
      // so it does not disturb the main seed stream.
      founderNames: makeFounderNames(ownRng),
      countryOfHeritage: pick(ownRng, HERITAGE_COUNTRIES),
      blackOwnershipEvidenceUrl: `https://verify.cashblack.example/ownership/${slugify(name)}`,
      ownershipVerificationStatus: pick(ownRng, OWNERSHIP_STATUSES),
      createdAt: daysAgo(randInt(rng, 30, 900)),
    };
    merchants.push(merchant);

    // Afrofiliate is the sole tracking platform (D-afrofiliate): every brand
    // tracks through it unless it is explicitly listed as legacy on another
    // network, in which case it stays on that legacy network as reference data.
    const legacySlug = LEGACY_NETWORK_BY_MERCHANT[merchant.slug];
    const merchantNetwork =
      affiliateNetworks.find((n) => n.slug === (legacySlug ?? 'afrofiliate')) ??
      null;

    // Markets: most brands are in both uk & us; some single-market.
    const roll = rng();
    const markets: Market[] =
      roll < 0.5 ? ['uk', 'us'] : roll < 0.8 ? ['uk'] : ['us'];

    for (const market of markets) {
      const net = merchantNetwork;
      const netDefault = net
        ? affiliateNetworkMarkets.find(
            (m) => m.networkId === net.id && m.market === market,
          )
        : undefined;
      const networkCommissionRate = net
        ? round4((netDefault?.defaultCommissionRate ?? 6) + (rng() - 0.5) * 2)
        : null;
      const overridden = chance(rng, 0.25);
      // Member rate is a share of the commission the network pays us.
      const baseRate = overridden
        ? round4(randInt(rng, 1, 8) + rng())
        : round4((networkCommissionRate ?? randInt(rng, 3, 9)) * 0.7);

      const region: MerchantRegion = {
        id: ++regionId,
        merchantId: merchant.id,
        market,
        baseRate,
        baseRateOverridden: overridden,
        networkId: net?.id ?? null,
        networkCommissionRate,
        regionTerms: `Valid for ${market.toUpperCase()} members only.`,
        active: merchant.status !== 'archived',
        // Regions rarely override the merchant's methods.
        trackingMethodOverride: chance(rng, 0.1)
          ? pick(rng, TRACKING_METHODS)
          : null,
        attributionMethodOverride: chance(rng, 0.1)
          ? pick(rng, ATTRIBUTION_METHODS)
          : null,
        tagKeys: pickTagKeys(rng, storeTags),
        // Boost is set in a dedicated pass below (from an independent PRNG), so
        // adding the Earnings boost concept leaves the main stream untouched.
        boost: null,
        createdAt: merchant.createdAt,
      };
      merchantRegions.push(region);

      // Tag join rows mirror the denormalised tagKeys.
      for (const key of region.tagKeys) {
        const tag = storeTags.find((t) => t.key === key);
        if (tag) {
          merchantRegionTags.push({
            id: ++mrTagId,
            merchantRegionId: region.id,
            tagId: tag.id,
          });
        }
      }

      // Channels: always a 'direct' channel; sometimes a marketplace too. There
      // is no tracking_method on a channel (that's on the merchant); a channel
      // may override the attribution method.
      const merchantUrl = `https://www.${merchant.slug}.com`;
      const directUrl = net
        ? `https://track.${net.trackingDomain}/click?` +
          `${net.clickParam}=${randInt(rng, 100000, 999999)}&` +
          `${net.deeplinkParam}=${encodeURIComponent(merchantUrl)}`
        : merchantUrl;
      storeChannels.push({
        id: ++channelId,
        merchantRegionId: region.id,
        market,
        channelType: 'direct',
        label: CHANNEL_TYPE_LABELS.direct,
        baseRate,
        trackingUrl: directUrl,
        attributionMethodOverride: null,
        isActive: region.active,
        createdAt: region.createdAt,
      });
      if (chance(rng, 0.3)) {
        const channelType = pick(rng, CHANNEL_TYPES);
        storeChannels.push({
          id: ++channelId,
          merchantRegionId: region.id,
          market,
          channelType,
          label: CHANNEL_TYPE_LABELS[channelType],
          baseRate: round4(baseRate + (rng() - 0.5)),
          // Marketplace/in-store channels mint the URL at click time (nullable).
          trackingUrl: null,
          attributionMethodOverride: chance(rng, 0.5)
            ? 'auto_subnetwork'
            : null,
          isActive: region.active,
          createdAt: region.createdAt,
        });
      }
    }
  });

  const merchantsById = new Map(merchants.map((m) => [m.id, m]));
  const resolveAttribution = (
    region: MerchantRegion,
    channel: StoreChannel | null,
  ): AttributionMethod =>
    channel?.attributionMethodOverride ??
    region.attributionMethodOverride ??
    merchantsById.get(region.merchantId)!.attributionMethod;

  const regionsByMarket: Record<Market, MerchantRegion[]> = {
    uk: merchantRegions.filter((r) => r.market === 'uk'),
    us: merchantRegions.filter((r) => r.market === 'us'),
    ie: [],
  };
  const channelsByRegion = new Map<number, StoreChannel[]>();
  for (const ch of storeChannels) {
    const list = channelsByRegion.get(ch.merchantRegionId) ?? [];
    list.push(ch);
    channelsByRegion.set(ch.merchantRegionId, list);
  }

  // Users (52).
  const users: User[] = [];
  for (let i = 0; i < 52; i++) {
    const first = pick(rng, FIRST_NAMES);
    const last = pick(rng, LAST_NAMES);
    const market = marketFor(rng);
    // Captured so the legacy original-signup date can be made OLDER than the
    // new-platform createdAt (a migrated member joined earlier).
    const createdDays = randInt(rng, 10, 1100);
    // Legacy marker (D-legacy-users) — drawn from `legacyRng` so the main seed
    // stream is unperturbed. ~35% are migrated from the old platform; the rest
    // are new (the two legacy fields null).
    const isLegacy = chance(legacyRng, 0.35);
    const legacySignupAt = isLegacy
      ? daysAgo(createdDays + randInt(legacyRng, 180, 1800))
      : null;
    const legacyUserId = isLegacy
      ? `legacy-${randInt(legacyRng, 100000, 999999)}`
      : null;
    const tier = chance(rng, 0.3) ? 'plus' : 'free';
    // Plus subscription fields (D-subscriptions), drawn from the independent
    // `subRng` so the main stream is unperturbed. State is kept consistent with
    // tier: a `plus` member currently holds Plus (active, or pending_cancel when
    // they have turned auto-renew off); a `free` member does not (none, or a
    // lapsed state if they held it before). No Stripe, no card billing anywhere.
    const subRoll = subRng();
    const periodDays = randInt(subRng, 1, 30);
    const billingRef = `plus-${randInt(subRng, 100000, 999999)}`;
    let plusStatus: PlusStatus;
    let currentPeriodEnd: string | null;
    let autoRenew: boolean;
    let billingReference: string | null;
    if (tier === 'plus') {
      plusStatus = subRoll < 0.8 ? 'active' : 'pending_cancel';
      autoRenew = plusStatus === 'active';
      currentPeriodEnd = daysAhead(periodDays);
      billingReference = billingRef;
    } else {
      plusStatus =
        subRoll < 0.7
          ? 'none'
          : subRoll < 0.85
            ? 'lapsed'
            : 'lapsed_insufficient';
      autoRenew = false;
      currentPeriodEnd = plusStatus === 'none' ? null : daysAgo(periodDays);
      billingReference = plusStatus === 'none' ? null : billingRef;
    }
    // Referral fields (D-referrals), from the independent `refRng` so the main
    // stream stays byte-identical. About a third of users (after the first few,
    // so a referrer already exists) were referred by an EARLIER user id; reward
    // state is the engine-written lifecycle. Cross-market pairs are allowed.
    let referredBy: number | null = null;
    let referralRewardStatus: ReferralRewardStatus | null = null;
    let referredAt: string | null = null;
    if (i >= 3 && chance(refRng, 0.32)) {
      referredBy = randInt(refRng, 1, i); // an earlier user (ids 1..i; this is i+1)
      const r = refRng();
      referralRewardStatus =
        r < 0.5 ? 'pending' : r < 0.7 ? 'qualified' : r < 0.9 ? 'paid' : 'void';
      referredAt = daysAgo(createdDays);
    }
    // Fraud fields (D-fraud-queue), from the independent `fraudRng` so the main
    // stream stays byte-identical. Most users are low-risk and active; a minority
    // score high, and a few of those carry a recorded fraud decision (suspended
    // with a live period, or banned). riskScore is the engine-written stand-in.
    const riskScore = randInt(fraudRng, 0, 100);
    let fraudStatus: FraudStatus = 'active';
    let suspensionEndsAt: string | null = null;
    let fraudReason: string | null = null;
    let escalated = false;
    if (riskScore >= 80) {
      const decision = fraudRng();
      if (decision < 0.3) {
        fraudStatus = 'suspended';
        suspensionEndsAt = daysAhead(randInt(fraudRng, 3, 30));
        fraudReason = 'Multiple high-value orders flagged for review';
      } else if (decision < 0.45) {
        fraudStatus = 'banned';
        fraudReason = 'Confirmed chargeback abuse';
      } else if (decision < 0.6) {
        escalated = true;
        fraudReason = 'Escalated for senior review';
      }
    }
    // Suspension-flow lifecycle (D-suspension-flow), from the independent `suspRng`.
    // A held account (suspended or banned) carries a structured reason code; some
    // of those held accounts have a pending or resolved appeal so the worklist and
    // the appeal paths render against real data. An active account carries none.
    let suspensionReasonCode: SuspensionReasonCode | null = null;
    let appealStatus: AppealStatus = 'none';
    let appealedAt: string | null = null;
    let appealNote: string | null = null;
    if (fraudStatus === 'suspended' || fraudStatus === 'banned') {
      suspensionReasonCode = pick(suspRng, [...SUSPENSION_REASON_CODES]);
      const appeal = suspRng();
      if (appeal < 0.35) {
        appealStatus = 'pending';
        appealedAt = daysAgo(randInt(suspRng, 1, 20));
        appealNote = 'I did not recognise these orders and want a review';
      } else if (appeal < 0.55) {
        appealStatus = 'resolved';
        appealedAt = daysAgo(randInt(suspRng, 20, 60));
        appealNote = 'Reviewed and the original decision was upheld';
      }
    }
    // Ban-evasion identity signals (D-ban-evasion). Engine-written stand-ins, here
    // derived deterministically from the row index (NO rng draw) so every value is
    // unique and the main seed stream stays byte-identical. The seed plants no
    // cross-account collisions (the dedicated ban-evasion fixture does that); the
    // live data shows no detected links, which is honest.
    const deviceId = `dev-${(i + 1).toString().padStart(4, '0')}`;
    const ipAddress = `10.0.${Math.floor(i / 256)}.${i % 256}`;
    const signupAddress = `${i + 1} Mock Street, ${market.toUpperCase()}`;
    users.push({
      id: i + 1,
      name: `${first} ${last}`,
      email: `${first}.${last}.${i + 1}`.toLowerCase() + '@example.com',
      market,
      tier,
      signupMethod: chance(rng, 0.55) ? 'social' : 'email',
      isLegacy,
      legacySignupAt,
      legacyUserId,
      plusStatus,
      currentPeriodEnd,
      autoRenew,
      billingReference,
      referredBy,
      referralRewardStatus,
      referredAt,
      riskScore,
      fraudStatus,
      suspensionEndsAt,
      fraudReason,
      escalated,
      suspensionReasonCode,
      appealStatus,
      appealedAt,
      appealNote,
      deviceId,
      ipAddress,
      signupAddress,
      linkDismissals: [],
      createdAt: daysAgo(createdDays),
    });
  }
  // Clicks + transactions, kept market-consistent. Attribution and the
  // effective rate are snapshotted on the click (not the transaction).
  const clicks: Click[] = [];
  let clickId = 0;
  const newClick = (
    user: User,
    region: MerchantRegion,
    converted: boolean,
    age: number,
    rate: number,
    attribution: AttributionMethod,
  ): number => {
    clicks.push({
      id: ++clickId,
      userId: user.id,
      merchantId: region.merchantId,
      merchantRegionId: region.id,
      market: user.market,
      attributionMethod: attribution,
      rateSnapshot: round4(rate),
      // occurredAt is the real click moment (D-store-performance EXTENSION). It is
      // seeded to the SAME value as the age basis (daysAgo(age)) so adding it
      // perturbs nothing; the store performance maturation cutoff reads this field.
      occurredAt: daysAgo(age),
      createdAt: daysAgo(age),
      converted,
    });
    return clickId;
  };

  const transactions: Transaction[] = [];
  for (let i = 0; i < 130; i++) {
    const user = pick(rng, users);
    const region = pick(rng, regionsByMarket[user.market]);
    const channels = channelsByRegion.get(region.id) ?? [];
    const channel = channels.length ? pick(rng, channels) : null;
    const rate = channel?.baseRate ?? region.baseRate;
    const attribution = resolveAttribution(region, channel);
    const grossAmount = round2(randInt(rng, 800, 40000) / 100);
    const cashbackAmount = round2((grossAmount * rate) / 100);
    const commissionAmount = round2(
      (grossAmount * (region.networkCommissionRate ?? rate * 1.4)) / 100,
    );
    const age = randInt(rng, 0, 365);
    const clickAttributed = chance(rng, 0.7);
    const status = pick(rng, TX_STATUSES);
    // Lifecycle timestamps from the independent `txRng` (main stream untouched).
    // Ordering: click (age+1 days ago) -> occurredAt (conversion, a few hours
    // after the click) -> createdAt (ingestion, ~a day later) -> confirmedAt
    // (confirmed rows only). expectedConfirmationAt is a forward projection for
    // in-flight (pending/waiting) rows.
    const createdAtMs = BASE_MS - age * DAY;
    const occurredAtMs = createdAtMs - DAY + randInt(txRng, 1, 6) * HOUR;
    const confirmedAt =
      status === 'confirmed'
        ? new Date(
            Math.min(occurredAtMs + randInt(txRng, 3, 45) * DAY, BASE_MS),
          ).toISOString()
        : null;
    const expectedConfirmationAt =
      status === 'pending' || status === 'waiting'
        ? new Date(occurredAtMs + randInt(txRng, 14, 60) * DAY).toISOString()
        : null;
    transactions.push({
      id: i + 1,
      userId: user.id,
      merchantId: region.merchantId,
      merchantRegionId: region.id,
      clickId: clickAttributed
        ? newClick(user, region, true, age + 1, rate, attribution)
        : null,
      market: user.market,
      grossAmount,
      cashbackAmount,
      commissionAmount,
      currency: CURRENCY_BY_MARKET[user.market],
      status,
      occurredAt: new Date(occurredAtMs).toISOString(),
      confirmedAt,
      expectedConfirmationAt,
      createdAt: daysAgo(age),
      // Normal transactions are attributed BY CONSTRUCTION (a member was known at
      // ingestion), so they carry attributionStatus 'attributed' and never enter
      // the manual-attribution queue (D-manual-attribution).
      attributionStatus: 'attributed',
    });
  }
  // Extra non-converted clicks for realistic click volumes.
  for (let i = 0; i < 90; i++) {
    const user = pick(rng, users);
    const region = pick(rng, regionsByMarket[user.market]);
    newClick(
      user,
      region,
      false,
      randInt(rng, 0, 365),
      region.baseRate,
      resolveAttribution(region, null),
    );
  }

  // Cashout requests (46). Payout vs donation split (D6). Guarantee at least
  // one settled donation ("donated" = donation + paid).
  const cashoutRequests: CashoutRequest[] = [];
  for (let i = 0; i < 46; i++) {
    const user = pick(rng, users);
    const isDonation = chance(rng, 0.3);
    const age = randInt(rng, 0, 180);
    const requestedAt = daysAgo(age);
    const status: CashoutRequest['status'] =
      i === 0
        ? 'paid' // force a settled donation below
        : pick(rng, [
            'requested',
            'processing',
            'paid',
            'paid',
            'failed',
            'cancelled',
          ] as const);
    const donation = i === 0 ? true : isDonation;
    const inFlight = status === 'requested' || status === 'processing';
    const slaTargetAt = daysAhead(2 - age); // 2 days after request
    const slaBreached = chance(rng, 0.15) && inFlight;
    cashoutRequests.push({
      id: i + 1,
      userId: user.id,
      amount: round2(randInt(rng, 500, 20000) / 100),
      currency: CURRENCY_BY_MARKET[user.market],
      market: user.market,
      destinationType: donation ? 'donation' : 'payout',
      payoutMethod: donation ? null : payoutMethodFor(rng, user.market),
      charityId: donation ? pick(rng, charitiesByMarket[user.market]).id : null,
      // Opaque "v1:<base64>" envelope for payouts; donations carry no rail.
      payoutDetails: donation
        ? null
        : `v1:${randInt(rng, 0, 0xffffffff).toString(36)}${randInt(rng, 0, 0xffffffff).toString(36)}`,
      status,
      slaTargetAt,
      slaBreachedAt: slaBreached ? daysAgo(age - 2) : null,
      requestedAt,
      paidAt:
        status === 'paid'
          ? daysAgo(Math.max(0, age - randInt(rng, 1, 3)))
          : null,
      // Member-renderable failure reason (backbone failed_reason), set iff failed.
      // A FIXED string, no rng draw, so the main seed stream stays byte-identical
      // (D-cashouts-manual-execution).
      failedReason:
        status === 'failed'
          ? 'The payout was returned by the receiving bank. Please check your details.'
          : null,
    });
  }

  // --- Balance ledger (Phase 5) ---------------------------------------------
  // The ONE shared signed ledger deriveBalanceSigned sums (D-balance-adjustment):
  // chargeback reversals AND manual adjustments both live here. A negative amount
  // debits the wallet, a positive amount credits it.
  const balanceLedger: BalanceLedgerEntry[] = [];
  let ledgerId = 0;

  // Chargebacks / disputes (D-chargebacks): a POST-PASS over confirmed transactions,
  // drawn from the independent `cbRng` so the main stream stays byte-identical. A
  // small fraction of confirmed orders are disputed across the lifecycle; a lost
  // dispute writes a NEGATIVE reversal entry (never a status flip), and the original
  // transaction stays confirmed. The dispute INTAKE (none to received) is external
  // (member / network / issuer), a cross-repo seam; the admin works it from received.
  for (const t of transactions) {
    if (t.status !== 'confirmed' || t.userId == null) continue;
    if (!chance(cbRng, 0.06)) continue;
    t.disputeSource = pick(cbRng, ['member', 'network', 'issuer']);
    t.disputeReasonCode = pick(cbRng, [...CHARGEBACK_REASON_CODES]);
    const roll = cbRng();
    if (roll < 0.3) {
      t.disputeStatus = 'received';
    } else if (roll < 0.55) {
      t.disputeStatus = 'under_review';
      t.disputeNote = 'Gathering the order and delivery evidence';
    } else if (roll < 0.78) {
      t.disputeStatus = 'won';
      t.disputeNote = 'Evidence accepted, dispute closed in our favour';
      t.resolvedAt = daysAgo(randInt(cbRng, 1, 30));
    } else {
      t.disputeStatus = 'lost';
      t.disputeNote = 'Dispute upheld against the platform';
      t.resolvedAt = daysAgo(randInt(cbRng, 1, 30));
      balanceLedger.push({
        id: ++ledgerId,
        userId: t.userId,
        transactionId: t.id,
        amount: -t.cashbackAmount,
        market: t.market,
        kind: 'chargeback_reversal',
        reasonCode: t.disputeReasonCode,
        reason: 'Chargeback resolved against the platform',
        createdAt: t.resolvedAt,
      });
    }
  }

  // Manual balance adjustments (D-balance-adjustment, the Phase 5 capstone): an
  // admin-recorded credit or debit on a member's wallet, tied to no transaction,
  // appended to the SAME ledger. Drawn from the independent `adjRng` AFTER the
  // chargeback pass so the reversals stay byte-identical and the main stream is
  // untouched. The first user (id 1) is deliberately left untouched so the provider's
  // balance-derivation unit test keeps its clean confirmed-minus-cashouts expectation.
  const ADJ_REASON_BY_DIRECTION = {
    credit: ['goodwill', 'missed_cashback'] as const,
    debit: ['over_credit_correction', 'erroneous_payment_recovery'] as const,
  };
  for (const user of users) {
    if (user.id === 1) continue;
    if (!chance(adjRng, 0.08)) continue;
    const isCredit = chance(adjRng, 0.6);
    // Modest amounts, well below any cap, so the live ledger has honest data without
    // distorting member balances. The dedicated fixture exercises the edge cases.
    const magnitude = round2(randInt(adjRng, 100, 1500) / 100);
    const reasonCode = pick(
      adjRng,
      isCredit ? ADJ_REASON_BY_DIRECTION.credit : ADJ_REASON_BY_DIRECTION.debit,
    );
    balanceLedger.push({
      id: ++ledgerId,
      userId: user.id,
      transactionId: null,
      amount: isCredit ? magnitude : -magnitude,
      market: user.market,
      kind: 'manual_adjustment',
      reasonCode,
      reason: isCredit
        ? 'Goodwill credit recorded by an admin'
        : 'Balance correction recorded by an admin',
      createdAt: daysAgo(randInt(adjRng, 1, 120)),
    });
  }

  // Support credits (D-support-role, the Phase 6 bounded credit): a handful of members
  // carry a support-agent credit, appended to the SAME ledger from an independent PRNG
  // so the main stream and the Phase 5 entries stay byte-identical. POSITIVE-ONLY and
  // modest (below the support cap). User 1 is left untouched so the provider's balance-
  // derivation unit test keeps its clean confirmed-minus-cashouts expectation.
  const supRng = mulberry32(SEED ^ 0x8f1bbcdc);
  const SUPPORT_CREDIT_REASONS = [
    'goodwill',
    'missed_cashback',
    'refund',
    'service_failure',
  ] as const;
  for (const user of users) {
    if (user.id === 1) continue;
    if (!chance(supRng, 0.06)) continue;
    balanceLedger.push({
      id: ++ledgerId,
      userId: user.id,
      transactionId: null,
      // A positive credit, well below the support cap.
      amount: round2(randInt(supRng, 100, 40000) / 100),
      market: user.market,
      kind: 'support_credit',
      reasonCode: pick(supRng, SUPPORT_CREDIT_REASONS),
      reason: 'Support credit recorded by an agent',
      createdAt: daysAgo(randInt(supRng, 1, 90)),
    });
  }

  // Support tickets (D-ticket-inbox, the Phase 6 lead): a worklist of member tickets,
  // drawn from an independent PRNG so the main stream stays byte-identical. A spread of
  // statuses, sources, assignment and SLA windows. A system-generated ticket links to a
  // failed cashout when the member has one. The live surface windows on the real clock;
  // the dedicated ticketsFixture exercises the SLA-breach derivation with an injected now.
  const ticketRng = mulberry32(SEED ^ 0x4a7f159c);
  const TICKET_STATUSES = [
    'open',
    'open',
    'pending',
    'escalated',
    'resolved',
  ] as const;
  const TICKET_SUBJECTS = [
    'Cannot see my cashback',
    'Payout did not arrive',
    'Refund request',
    'Login issue',
    'Cashback declined on a return',
  ] as const;
  const failedCashoutByUser = new Map<number, number>();
  for (const c of cashoutRequests) {
    if (c.status === 'failed' && !failedCashoutByUser.has(c.userId)) {
      failedCashoutByUser.set(c.userId, c.id);
    }
  }
  const tickets: Ticket[] = [];
  let ticketId = 0;
  for (const user of users) {
    if (!chance(ticketRng, 0.18)) continue;
    const status = pick(ticketRng, TICKET_STATUSES);
    const failedId = failedCashoutByUser.get(user.id);
    const system = failedId != null && chance(ticketRng, 0.5);
    tickets.push({
      id: ++ticketId,
      userId: user.id,
      market: user.market,
      status,
      assignee: chance(ticketRng, 0.5) ? `agent-${user.market}` : null,
      source: system ? 'system_generated' : 'user_initiated',
      triggeringEntity: system ? { kind: 'cashout', id: failedId } : null,
      subject: pick(ticketRng, TICKET_SUBJECTS),
      body: 'Member support ticket awaiting handling.',
      slaTargetAt: daysAhead(randInt(ticketRng, -10, 10)),
      createdAt: daysAgo(randInt(ticketRng, 1, 60)),
    });
  }

  // Canned responses + macros (D-canned-responses, the Phase 6 surface-2 library): a
  // SHARED GLOBAL library of reusable response templates with variable substitution, plus
  // macros (template + a capability-guarded ticket transition). Drawn from an independent
  // PRNG so adding the collection leaves the main stream byte-identical. NOT market-tagged
  // in v1 (uk/us copy share). A plain response, two macros (resolve / escalate), and a
  // retired (inactive) template so the deactivate-not-delete state has live data.
  const cannedRng = mulberry32(SEED ^ 0xca62c1d6);
  const cannedResponses: CannedResponse[] = [
    {
      id: 1,
      name: 'Missing cashback explainer',
      body: 'Hi {member_name}, your balance is {balance}. We are looking into the missing cashback on your {market} account and will update you on {ticket_subject}.',
      kind: 'response',
      action: null,
      active: true,
      createdAt: daysAgo(randInt(cannedRng, 30, 120)),
    },
    {
      id: 2,
      name: 'Resolved and reply',
      body: 'Hi {member_name}, this is now resolved. Your balance is {balance}. Thank you for your patience.',
      kind: 'macro',
      action: { capability: 'tickets:resolve', toStatus: 'resolved' },
      active: true,
      createdAt: daysAgo(randInt(cannedRng, 30, 120)),
    },
    {
      id: 3,
      name: 'Escalate to a specialist',
      body: 'Hi {member_name}, we are routing this to a specialist who will follow up shortly.',
      kind: 'macro',
      action: { capability: 'tickets:escalate', toStatus: 'escalated' },
      active: true,
      createdAt: daysAgo(randInt(cannedRng, 30, 120)),
    },
    {
      id: 4,
      name: 'Retired welcome script',
      body: 'An older template kept for reference only.',
      kind: 'response',
      action: null,
      active: false,
      createdAt: daysAgo(randInt(cannedRng, 200, 400)),
    },
  ];

  // Impersonation sessions (D-impersonation, the Phase 6 surface-4 close): a few RECORDED
  // view-only sessions, drawn from an independent PRNG so the main stream stays byte-
  // identical. All anchored in the past (the seed's BASE_MS), so against the live clock they
  // are ENDED or EXPIRED: there is deliberately no permanently-active session on load (no one
  // is mid-impersonation in a fresh demo). The dedicated fixture injects its own clock for
  // the active case. A 15-minute window matches IMPERSONATION_SESSION_DURATION.
  const imprRng = mulberry32(SEED ^ 0x71374491);
  const IMPERSONATION_WINDOW_MS = 15 * 60 * 1000;
  const impersonationSessions: ImpersonationSession[] = [];
  let imprId = 0;
  for (let k = 0; k < 4; k++) {
    const user = pick(imprRng, users);
    const startedMs = BASE_MS - randInt(imprRng, 2, 120) * DAY;
    const ended = chance(imprRng, 0.5);
    impersonationSessions.push({
      id: ++imprId,
      subjectUserId: user.id,
      market: user.market,
      startedAt: new Date(startedMs).toISOString(),
      expiresAt: new Date(startedMs + IMPERSONATION_WINDOW_MS).toISOString(),
      // Some were ended explicitly a few minutes in; the rest lapsed by expiry (no write).
      endedAt: ended
        ? new Date(
            startedMs + randInt(imprRng, 1, 14) * 60 * 1000,
          ).toISOString()
        : null,
    });
  }

  // Data-subject access requests (D-dsar, the Phase 7 lead): a few RECORDED DSARs across
  // every lifecycle status, drawn from an independent PRNG so adding the collection leaves
  // the main stream byte-identical. Anchored in the seed's past (BASE_MS), so the live
  // surface windows them on the real clock; the dedicated fixture injects its own clock to
  // exercise the derived statutory-deadline tri-state (approaching / breached / on_track)
  // precisely. The statutory window is a [REVIEW] product constant carried in dsarState.
  const dsarRng = mulberry32(SEED ^ 0xe9b5dba5);
  const DSAR_SUBJECTS = users.slice(0, 5);
  const DSAR_STATUSES: readonly DsarStatus[] = [
    'received',
    'received',
    'in_progress',
    'fulfilled',
    'rejected',
  ];
  const dsarRequests: DsarRequest[] = DSAR_SUBJECTS.map((subject, i) => {
    const status = DSAR_STATUSES[i];
    const requestedMs = BASE_MS - randInt(dsarRng, 3, 28) * DAY;
    const requestedAt = new Date(requestedMs).toISOString();
    return {
      id: i + 1,
      subjectUserId: subject.id,
      market: subject.market,
      status,
      requestedAt,
      fulfilledAt:
        status === 'fulfilled'
          ? new Date(requestedMs + randInt(dsarRng, 1, 20) * DAY).toISOString()
          : null,
      rejectedAt:
        status === 'rejected'
          ? new Date(requestedMs + randInt(dsarRng, 1, 10) * DAY).toISOString()
          : null,
      rejectionReason:
        status === 'rejected'
          ? 'Identity could not be verified to the standard a data release requires'
          : null,
    };
  });

  // Consent records (D-consent-records, the Phase 7 surface 2): an APPEND-ONLY history of
  // grants and revocations, drawn from an independent PRNG so adding the collection leaves the
  // main stream byte-identical. The standing consent per type is DERIVED on read as the latest
  // record (never stored), so the history is the single source of truth. The seed gives a
  // spread of types, sources and policy versions, and at least one type GRANTED then REVOKED
  // then RE-GRANTED (the integrity case the derivation must get right). The dedicated fixture
  // shapes the precise assertions with its own clock.
  const consentRng = mulberry32(SEED ^ 0xc67178f2);
  const CONSENT_SUBJECTS = users.slice(0, 6);
  const POLICY_VERSIONS = ['2024-01', '2025-03'] as const;
  const consents: ConsentRecord[] = [];
  let consentId = 0;
  const addConsent = (
    user: (typeof users)[number],
    consentType: ConsentType,
    action: 'grant' | 'revoke',
    source: ConsentRecord['source'],
    daysAgoVal: number,
    note: string | null = null,
  ): void => {
    consents.push({
      id: ++consentId,
      userId: user.id,
      market: user.market,
      consentType,
      action,
      source,
      policyVersion: daysAgoVal > 200 ? POLICY_VERSIONS[0] : POLICY_VERSIONS[1],
      note,
      occurredAt: daysAgo(daysAgoVal),
    });
  };
  for (const user of CONSENT_SUBJECTS) {
    const base = randInt(consentRng, 220, 360);
    // At signup the member grants the baseline consents.
    addConsent(user, 'data_processing', 'grant', 'signup', base);
    addConsent(user, 'age_confirmation', 'grant', 'signup', base);
    addConsent(user, 'cookies', 'grant', 'signup', base);
    addConsent(user, 'marketing_email', 'grant', 'signup', base);
    // A later settings change: roughly half revoke marketing email.
    if (chance(consentRng, 0.5)) {
      addConsent(
        user,
        'marketing_email',
        'revoke',
        'settings',
        randInt(consentRng, 30, 150),
      );
    }
    // The integrity case for the first subject: push consent granted, revoked, re-granted, so
    // the derived current state is GRANTED (the latest), never the first record.
    if (user.id === CONSENT_SUBJECTS[0].id) {
      addConsent(user, 'marketing_push', 'grant', 'signup', base);
      addConsent(user, 'marketing_push', 'revoke', 'settings', 120);
      addConsent(user, 'marketing_push', 'grant', 'consumer_app', 20);
      // A withdrawal recorded off-app by compliance (the one narrow admin write, seeded here).
      addConsent(user, 'third_party_sharing', 'grant', 'settings', base - 10);
      addConsent(
        user,
        'third_party_sharing',
        'revoke',
        'compliance_logged',
        15,
        'Member phoned the support line and asked to stop all data sharing',
      );
    }
  }

  // Sanctions matches (D-sanctions, the Phase 7 surface 3): a few ENGINE-WRITTEN match stand-ins
  // across every lifecycle state and list, drawn from an independent PRNG so adding the collection
  // leaves the main stream byte-identical. There is no real list and no admin run-a-screen action:
  // the engine produces the matches, the admin reviews them (the riskScore stand-in pattern). The
  // dedicated fixture shapes the precise assertions with its own clock.
  const sanctionRng = mulberry32(SEED ^ 0x5be0cd19);
  const SANCTION_SUBJECTS = users.slice(0, 6);
  const SANCTION_LISTS: readonly SanctionsList[] = ['ofac', 'uk_hmt', 'pep'];
  const SANCTION_STATUSES: readonly SanctionsStatus[] = [
    'pending_review',
    'pending_review',
    'cleared',
    'confirmed',
  ];
  const SANCTIONED_NAMES = [
    'Viktor Petrov',
    'Ahmed Al-Rashid',
    'Ivan Volkov',
    'Chen Wei',
    'Dmitri Sokolov',
    'Omar Hassan',
  ];
  const SANCTION_REVIEW_REASON: Record<SanctionsStatus, string | null> = {
    pending_review: null,
    cleared: 'Date of birth and nationality do not match the listed individual',
    confirmed: 'Identity confirmed against the listed individual',
  };
  const sanctionsMatches: SanctionsMatch[] = SANCTION_SUBJECTS.map(
    (subject, i) => {
      const status = SANCTION_STATUSES[i % SANCTION_STATUSES.length];
      const screenedMs = BASE_MS - randInt(sanctionRng, 2, 45) * DAY;
      const resolved = status !== 'pending_review';
      return {
        id: i + 1,
        userId: subject.id,
        market: subject.market,
        matchedList: SANCTION_LISTS[i % SANCTION_LISTS.length],
        matchScore: randInt(sanctionRng, 45, 98),
        matchedName: SANCTIONED_NAMES[i % SANCTIONED_NAMES.length],
        screenedAt: new Date(screenedMs).toISOString(),
        sanctionsStatus: status,
        reviewReason: SANCTION_REVIEW_REASON[status],
        reviewedAt: resolved
          ? new Date(
              screenedMs + randInt(sanctionRng, 1, 10) * DAY,
            ).toISOString()
          : null,
      };
    },
  );

  // Erasure / right-to-be-forgotten (D-erasure, the Phase 7 surface 6, the last and sharpest): a few
  // requests across the lifecycle over existing members, drawn from an independent PRNG so adding the
  // collection leaves the main stream byte-identical. Anchored in the seed's past (BASE_MS), so against
  // the live clock the grace windows have elapsed; the dedicated fixture shapes the precise in-grace vs
  // elapsed assertions with its own injected clock. There is NO deletion engine: a request is a
  // recorded decision, execute is a recorded decision plus a minimal owned-row tombstone, the real
  // cascade a backend seam.
  const erasureRng = mulberry32(SEED ^ 0x9e3779b9);
  const ERASURE_SUBJECTS = users.slice(6, 11);
  const ERASURE_STATES: readonly ErasureStatus[] = [
    'requested',
    'requested',
    'executed',
    'cancelled',
    'rejected',
  ];
  const erasureRequests: ErasureRequest[] = ERASURE_SUBJECTS.map(
    (subject, i) => {
      const status = ERASURE_STATES[i % ERASURE_STATES.length];
      const requestedMs = BASE_MS - randInt(erasureRng, 5, 120) * DAY;
      const resolvedMs = requestedMs + randInt(erasureRng, 1, 20) * DAY;
      return {
        id: i + 1,
        subjectUserId: subject.id,
        market: subject.market,
        status,
        requestedAt: new Date(requestedMs).toISOString(),
        executedAt:
          status === 'executed' ? new Date(resolvedMs).toISOString() : null,
        cancelledAt:
          status === 'cancelled' ? new Date(resolvedMs).toISOString() : null,
        rejectedAt:
          status === 'rejected' ? new Date(resolvedMs).toISOString() : null,
        rejectionReason:
          status === 'rejected'
            ? 'Member is under an active fraud hold; the data is retained on a legal basis'
            : null,
      };
    },
  );

  // Tax reporting / 1099-K (D-tax-reporting, the Phase 7 surface 4): the surface is a DERIVED read
  // over paid US payouts (which US members crossed the IRS reporting threshold for a tax year). The
  // organic seed payouts are $5 to $200, so no member crosses any threshold; append a couple of
  // large PAID payouts for existing US members so the LIVE surface shows reportable members. Drawn
  // from an independent PRNG so the main stream stays byte-identical, and dated in the most-recent
  // COMPLETE calendar year (2025) so the surface's default tax year surfaces them. The dedicated
  // fixture (taxFixture.ts) shapes the precise assertions with its own injected clock.
  const taxRng = mulberry32(SEED ^ 0xa54ff53a);
  const usMembers = users.filter((u) => u.market === 'us').slice(0, 2);
  let taxCashoutId = cashoutRequests.length;
  for (const member of usMembers) {
    // Two paid payouts in 2025 summing well over the 2025 reporting threshold.
    for (let k = 0; k < 2; k++) {
      taxCashoutId += 1;
      const paidMs = Date.UTC(
        2025,
        randInt(taxRng, 0, 4),
        randInt(taxRng, 1, 28),
      );
      cashoutRequests.push({
        id: taxCashoutId,
        userId: member.id,
        amount: round2(randInt(taxRng, 150000, 220000) / 100),
        currency: CURRENCY_BY_MARKET[member.market],
        market: member.market,
        destinationType: 'payout',
        payoutMethod: 'us_ach',
        charityId: null,
        payoutDetails: `v1:${randInt(taxRng, 0, 0xffffffff).toString(36)}`,
        status: 'paid',
        slaTargetAt: new Date(paidMs - 2 * DAY).toISOString(),
        slaBreachedAt: null,
        requestedAt: new Date(paidMs - 3 * DAY).toISOString(),
        paidAt: new Date(paidMs).toISOString(),
        failedReason: null,
      });
    }
  }

  // --- Earnings enrichment (Phase 3) ----------------------------------------
  // Boosts and welcome-bonus fundings are drawn from their OWN independent PRNG
  // (as ownership / legacy were before them), so adding the Earnings concepts
  // leaves every other seeded value byte-for-byte unchanged.
  //
  // Note: the seed is anchored at BASE_MS (2025-06-01); the live surface windows
  // on the real clock, so these dates sit in the surface's past. The behaviours
  // (live boosts, welcome divergence, the trend gate) are exercised exactly by
  // the dedicated fixture in src/earnings/earningsFixture.ts, which injects its
  // own `now`. Here we only keep the live collections internally consistent.
  const earnRng = mulberry32(SEED ^ 0x85ebca6b);

  // Live rate boosts on a few active regions (D-earnings-boost). A boosted
  // region's thin retained margin is intentional; the surface marks it.
  const BOOST_POOL = [
    { label: 'Black Friday boost', reasonCode: 'seasonal_bf' },
    { label: 'New partner launch', reasonCode: 'launch' },
    { label: 'Heritage Month feature', reasonCode: 'heritage_month' },
  ] as const;
  for (const region of merchantRegions) {
    if (region.active && chance(earnRng, 0.06)) {
      const b = pick(earnRng, BOOST_POOL);
      region.boost = {
        label: b.label,
        reasonCode: b.reasonCode,
        endsAt: daysAhead(randInt(earnRng, 5, 40)),
      };
    }
  }

  // Welcome-bonus fundings: a platform cost funded at withdrawal
  // (D-earnings-welcome). Funded once per member, for a minority of members,
  // sized so per-market contribution visibly diverges from confirmed margin.
  const welcomeBonusFundings: WelcomeBonusFunding[] = [];
  let wbfId = 0;
  for (const user of users) {
    if (chance(earnRng, 0.22)) {
      welcomeBonusFundings.push({
        id: ++wbfId,
        userId: user.id,
        market: user.market,
        currency: CURRENCY_BY_MARKET[user.market],
        amount: round2(randInt(earnRng, 300, 1000) / 100),
        fundedAt: daysAgo(randInt(earnRng, 0, 200)),
      });
    }
  }

  // Promo codes catalogue (D-promo-codes). Definitions only: redemption is not
  // modelled, and usedCount is a DISPLAYED number a future engine writes back. One
  // market per code. Drawn from promoRng so the main stream stays byte-identical.
  let promoId = 0;
  const newPromo = (
    code: string,
    market: Market,
    effectValue: number,
    validFrom: string,
    validTo: string,
    maxRedemptions: number | null,
    usedCount: number,
    active: boolean,
  ): PromoCode => ({
    id: ++promoId,
    code,
    market,
    effectType: 'fixed_wallet_credit',
    effectValue,
    validFrom,
    validTo,
    maxRedemptions,
    usedCount,
    active,
    createdAt: daysAgo(randInt(promoRng, 30, 200)),
  });
  const promoCodes: PromoCode[] = [
    // uk (GBP): a live capped code, a live boost, an expired code, an inactive one.
    newPromo(
      'UKWELCOME5',
      'uk',
      5,
      daysAgo(60),
      daysAhead(120),
      1000,
      randInt(promoRng, 100, 400),
      true,
    ),
    newPromo(
      'UKBOOST10',
      'uk',
      10,
      daysAgo(20),
      daysAhead(40),
      200,
      randInt(promoRng, 0, 150),
      true,
    ),
    newPromo(
      'UKLAPSED5',
      'uk',
      5,
      daysAgo(200),
      daysAgo(30),
      500,
      randInt(promoRng, 50, 200),
      true,
    ),
    newPromo('UKRETIRED', 'uk', 5, daysAgo(300), daysAgo(120), 100, 100, false),
    // us (USD): a live capped code and a live UNCAPPED one (cap optional).
    newPromo(
      'USWELCOME5',
      'us',
      5,
      daysAgo(60),
      daysAhead(120),
      1000,
      randInt(promoRng, 100, 400),
      true,
    ),
    newPromo(
      'USBOOST7',
      'us',
      7,
      daysAgo(10),
      daysAhead(60),
      null,
      randInt(promoRng, 0, 300),
      true,
    ),
  ];

  // Placements catalogue (D-placements). Internal merchandising: a brand featured
  // in a SLOT for a market over a WINDOW with a WEIGHT (the share-of-voice
  // arbitration). NO price (not sold inventory) and NO active flag (a placement is
  // ENDED via its window, not soft-disabled). One market per row. Drawn from
  // placeRng so the main stream stays byte-identical. merchantId references the
  // BRAND (merchants.id); the slot is the fixed enum.
  const SLOTS: readonly PlacementSlot[] = [
    'homepage_hero',
    'app_push_target',
    'email_feature',
  ];
  const activeMerchants = merchants.filter((m) => m.status === 'active');
  const placements: Placement[] = [];
  let placeId = 0;
  for (const market of ['uk', 'us'] as const) {
    for (const slot of SLOTS) {
      // One to three brands compete for each slot in each market, with differing
      // weights, so the surface's overlap + share-of-voice derivation has real
      // co-occupancy to render.
      const competitors = randInt(placeRng, 1, 3);
      for (let n = 0; n < competitors; n++) {
        const merchant = pick(placeRng, activeMerchants);
        // A spread of windows so each lifecycle state (scheduled / live / ended)
        // is represented across the catalogue.
        const startOffset = randInt(placeRng, -120, 40); // negative = already started
        const length = randInt(placeRng, 30, 120);
        const openEnded = chance(placeRng, 0.15);
        placements.push({
          id: ++placeId,
          merchantId: merchant.id,
          slot,
          market,
          weight: randInt(placeRng, 1, 5),
          startsAt: daysAhead(startOffset),
          endsAt: openEnded ? null : daysAhead(startOffset + length),
          createdAt: daysAgo(randInt(placeRng, 10, 120)),
        });
      }
    }
  }

  // Manual-attribution orphans (D-manual-attribution): transactions that arrived
  // with NO known member (userId null), awaiting manual crediting by ops. Market
  // comes from the MERCHANT side (the merchant_region), independent of any member,
  // so an orphan bands by market before it is credited. A spread of review states
  // so the live queue has data. Drawn from attrRng so the main stream is unchanged.
  const ORPHAN_STATES: readonly AttributionStatus[] = [
    'unattributed',
    'unattributed',
    'needs_review',
    'rejected',
  ];
  let orphanId = transactions.length;
  for (let k = 0; k < 8; k++) {
    // Alternate market so both bands carry orphans (merchant-derived market).
    const orphanMarket: Market = k % 2 === 0 ? 'uk' : 'us';
    const region = pick(attrRng, regionsByMarket[orphanMarket]);
    const channels = channelsByRegion.get(region.id) ?? [];
    const channel = channels.length ? pick(attrRng, channels) : null;
    const rate = channel?.baseRate ?? region.baseRate;
    const grossAmount = round2(randInt(attrRng, 800, 40000) / 100);
    const age = randInt(attrRng, 1, 60);
    // Same ordering as a normal transaction: occurredAt (conversion) sits a little
    // before createdAt (ingestion), so the lifecycle invariants hold for orphans too.
    const createdAtMs = BASE_MS - age * DAY;
    const occurredAtMs = createdAtMs - DAY + randInt(attrRng, 1, 6) * HOUR;
    transactions.push({
      id: ++orphanId,
      userId: null,
      merchantId: region.merchantId,
      merchantRegionId: region.id,
      clickId: null,
      market: region.market,
      grossAmount,
      cashbackAmount: round2((grossAmount * rate) / 100),
      commissionAmount: round2(
        (grossAmount * (region.networkCommissionRate ?? rate * 1.4)) / 100,
      ),
      currency: CURRENCY_BY_MARKET[region.market],
      status: 'pending',
      occurredAt: new Date(occurredAtMs).toISOString(),
      confirmedAt: null,
      expectedConfirmationAt: new Date(occurredAtMs + 30 * DAY).toISOString(),
      createdAt: daysAgo(age),
      attributionStatus: pick(attrRng, ORPHAN_STATES),
      matchedClickId: null,
      attributionNote: null,
    });
  }

  // Storefront marketplace channels (the channel-enum extension;
  // D-multi-channel-editor). Seeded from an INDEPENDENT prng AFTER every consumer
  // of channelsByRegion, so the main seed stream stays byte-identical: this only
  // appends rows to storeChannels. A small deterministic spread of etsy / amazon /
  // ebay / doordash so the live app shows the new types and the editor has them to
  // render and edit. Each respects the unique (merchantRegionId, channelType)
  // constraint and mirrors the region's market / active, like every other channel.
  const storefrontRng = mulberry32(SEED ^ 0x3956c25b);
  const storefrontTargets = merchantRegions.filter((r) => r.active).slice(0, 8);
  storefrontTargets.forEach((region, i) => {
    const channelType =
      STOREFRONT_CHANNEL_TYPES[i % STOREFRONT_CHANNEL_TYPES.length];
    const exists = storeChannels.some(
      (c) => c.merchantRegionId === region.id && c.channelType === channelType,
    );
    if (exists) return;
    storeChannels.push({
      id: ++channelId,
      merchantRegionId: region.id,
      market: region.market,
      channelType,
      label: CHANNEL_TYPE_LABELS[channelType],
      baseRate: round4(Math.max(0, region.baseRate + (storefrontRng() - 0.5))),
      // Marketplace channels mint the URL at click time (nullable).
      trackingUrl: null,
      attributionMethodOverride: null,
      isActive: region.active,
      createdAt: region.createdAt,
    });
  });

  // Rate rules (D-rate-rules, Phase 8 surface 2). Seeded from an INDEPENDENT prng
  // and appended last, so the main seed stream stays byte-identical. A spread
  // across the scope-type vocabulary, the lifecycle states (draft / active /
  // paused) and the window (live / scheduled / expired / permanent), referencing
  // the REAL catalog (etsy channels, tags, categories, region ids) so the scope
  // counts and the resolver bite on real entities. Like the earnings boosts, the
  // windows are anchored in the seed's past (BASE_MS); the LIVE lifecycle states
  // are exercised by the dedicated fixture (src/rateRules/rateRulesFixture.ts),
  // which injects its own clock. Includes a CONTESTED store (two active rules
  // matching one store at different priorities) for the conflict preview.
  const rateRng = mulberry32(SEED ^ 0x1f83d9ab);
  const etsyRegionIds = storeChannels
    .filter((c) => c.channelType === 'etsy')
    .map((c) => c.merchantRegionId);
  const contestedRegionId = etsyRegionIds[0] ?? merchantRegions[0].id;
  const sampleCategoryId = merchants[0].categoryId;
  const sampleTagKey = 'top_stores';
  const flagshipStoreId = merchantRegions[0].id;
  const author = 'nia.editor@cashblack.example';
  let rateRuleId = 0;
  const mkRateRule = (
    rule: Omit<RateRule, 'id' | 'createdBy' | 'createdAt' | 'updatedAt'>,
  ): RateRule => {
    const created = daysAgo(randInt(rateRng, 30, 200));
    return {
      ...rule,
      id: ++rateRuleId,
      createdBy: author,
      createdAt: created,
      updatedAt: created,
    } as RateRule;
  };
  const rateRules: RateRule[] = [
    mkRateRule({
      scopeType: 'marketplace',
      scopeRef: 'etsy',
      effectType: 'multiplier',
      effectValue: 1.5,
      tierEligibility: ['free', 'plus'],
      market: 'all',
      priority: 10,
      visibleFrom: daysAgo(20),
      visibleUntil: daysAhead(40),
      displayLabel: 'Etsy seasonal bump',
      displayReasonCode: 'seasonal_etsy',
      status: 'active',
    }),
    mkRateRule({
      scopeType: 'tag',
      scopeRef: sampleTagKey,
      effectType: 'multiplier',
      effectValue: 1.3,
      tierEligibility: ['free', 'plus'],
      market: 'uk',
      priority: 10,
      visibleFrom: null,
      visibleUntil: null,
      displayLabel: 'Top stores bump',
      displayReasonCode: 'top_stores',
      status: 'active',
    }),
    mkRateRule({
      scopeType: 'category',
      scopeRef: sampleCategoryId,
      effectType: 'multiplier',
      effectValue: 1.2,
      tierEligibility: ['plus'],
      market: 'all',
      priority: 15,
      visibleFrom: daysAgo(10),
      visibleUntil: daysAhead(50),
      displayLabel: 'Plus category boost',
      displayReasonCode: 'plus_category',
      status: 'active',
    }),
    mkRateRule({
      scopeType: 'store',
      scopeRef: flagshipStoreId,
      effectType: 'absolute',
      effectValue: 8,
      tierEligibility: ['free', 'plus'],
      market: 'uk',
      priority: 100,
      visibleFrom: null,
      visibleUntil: null,
      displayLabel: 'Flagship rate',
      displayReasonCode: 'flagship',
      status: 'active',
    }),
    mkRateRule({
      scopeType: 'store_list',
      scopeRef: [contestedRegionId, merchantRegions[1].id],
      effectType: 'multiplier',
      effectValue: 1.8,
      tierEligibility: ['free', 'plus'],
      market: 'all',
      priority: 30,
      visibleFrom: daysAgo(5),
      visibleUntil: daysAhead(20),
      displayLabel: 'Black Friday',
      displayReasonCode: 'black_friday',
      status: 'active',
    }),
    mkRateRule({
      scopeType: 'market',
      scopeRef: 'uk',
      effectType: 'cap',
      effectValue: 6,
      tierEligibility: ['free', 'plus'],
      market: 'uk',
      priority: 5,
      visibleFrom: null,
      visibleUntil: null,
      displayLabel: 'UK ceiling',
      displayReasonCode: 'uk_ceiling',
      status: 'active',
    }),
    mkRateRule({
      scopeType: 'marketplace_category',
      scopeRef: { channelType: 'etsy', categoryId: sampleCategoryId },
      effectType: 'multiplier',
      effectValue: 1.35,
      tierEligibility: ['free', 'plus'],
      market: 'us',
      priority: 40,
      visibleFrom: daysAgo(15),
      visibleUntil: daysAhead(30),
      displayLabel: 'US etsy category',
      displayReasonCode: 'us_etsy_cat',
      status: 'active',
    }),
    mkRateRule({
      scopeType: 'tag_marketplaces',
      scopeRef: { tag: sampleTagKey, marketplaces: ['deliveroo', 'uber_eats'] },
      effectType: 'multiplier',
      effectValue: 2.0,
      tierEligibility: ['free', 'plus'],
      market: 'uk',
      priority: 25,
      visibleFrom: daysAhead(10),
      visibleUntil: daysAhead(60),
      displayLabel: 'Future delivery boost',
      displayReasonCode: 'future_delivery',
      status: 'active',
    }),
    mkRateRule({
      scopeType: 'marketplace',
      scopeRef: 'amazon',
      effectType: 'multiplier',
      effectValue: 1.4,
      tierEligibility: ['free', 'plus'],
      market: 'all',
      priority: 20,
      visibleFrom: null,
      visibleUntil: null,
      displayLabel: 'Amazon draft',
      displayReasonCode: 'amazon_draft',
      status: 'draft',
    }),
    mkRateRule({
      scopeType: 'marketplace',
      scopeRef: 'deliveroo',
      effectType: 'multiplier',
      effectValue: 1.6,
      tierEligibility: ['free', 'plus'],
      market: 'uk',
      priority: 20,
      visibleFrom: daysAgo(30),
      visibleUntil: daysAhead(30),
      displayLabel: 'Deliveroo paused',
      displayReasonCode: 'deliveroo_paused',
      status: 'paused',
    }),
    mkRateRule({
      scopeType: 'marketplace',
      scopeRef: 'ebay',
      effectType: 'multiplier',
      effectValue: 1.5,
      tierEligibility: ['free', 'plus'],
      market: 'all',
      priority: 10,
      visibleFrom: daysAgo(120),
      visibleUntil: daysAgo(30),
      displayLabel: 'Past ebay promo',
      displayReasonCode: 'past_ebay',
      status: 'active',
    }),
  ];

  // Page schemas (D-page-schema-editor-8a, Phase 8 surface 3a). Seeded from an
  // INDEPENDENT prng and appended last, so the main seed stream stays byte-identical.
  // DRAFTS ONLY (no published version; that is 8b): a spread across the page-id
  // vocabulary (home / browse-featured / category-{slug} / seasonal-{name}) in BOTH
  // markets, with one draft carrying ALL NINE block types, the three REQUIRED-variant
  // blocks (carousel / categoryStrip / banner) and pillCta present, and store /
  // category references by SLUG that RESOLVE against the real catalog (so the
  // structural view shows real names, never a dangling id).
  const pageRng = mulberry32(SEED ^ 0x85ebca6b);
  const storeSlugs = merchants.map((m) => m.slug);
  const topCategorySlugs = categories
    .filter((c) => c.parentId === null)
    .map((c) => c.slug);
  const s = (i: number): string => storeSlugs[i % storeSlugs.length];
  const c = (i: number): string =>
    topCategorySlugs[i % topCategorySlugs.length];
  const pageAuthor = 'nia.editor@cashblack.example';
  let pageSchemaId = 0;
  const mkPage = (
    pageId: string,
    market: Market,
    blocks: Block[],
    publish = false,
  ): PageSchema => {
    const created = daysAgo(randInt(pageRng, 10, 120));
    // A published page (8b) carries an APPEND-ONLY history; the seed publishes v1
    // equal to the draft, so the page reads published-and-clean. Most pages stay
    // drafts (versions empty). The precise versioned states (draft-ahead, multi-entry
    // history, a dangling reference) are exercised by the dedicated fixture
    // (src/pageSchema/pageSchemasFixture.ts).
    const versions: PublishedVersion[] = publish
      ? [{ version: 1, blocks, publishedAt: created, publishedBy: pageAuthor }]
      : [];
    const latest = versions.length ? versions[versions.length - 1] : null;
    return {
      id: ++pageSchemaId,
      pageId,
      market,
      status: latest ? 'published' : 'draft',
      // version / publishedAt reflect the latest publish; a pure draft carries the
      // placeholders (0 / null).
      version: latest?.version ?? 0,
      publishedAt: latest?.publishedAt ?? null,
      versions,
      blocks,
      createdBy: pageAuthor,
      createdAt: created,
      updatedAt: created,
    };
  };
  const pageSchemas: PageSchema[] = [
    // The showcase: a uk home draft carrying ALL NINE block types.
    mkPage(
      'home',
      'uk',
      [
        {
          type: 'hero',
          id: 'home-uk-hero',
          data: {
            image: 'https://img.example/home-hero.jpg',
            title: 'Welcome to Cashblack',
            subtitle: 'Earn on every shop',
            cta: { label: 'Browse stores', link: '/browse' },
          },
        },
        {
          type: 'carousel',
          id: 'home-uk-carousel',
          data: {
            variant: 'seasonal',
            title: 'Seasonal picks',
            storeIds: [s(0), s(1)],
          },
        },
        {
          type: 'storeGrid',
          id: 'home-uk-grid',
          data: {
            title: 'Top stores',
            storeIds: [s(0), s(1), s(2)],
            columns: 3,
          },
        },
        {
          type: 'categoryStrip',
          id: 'home-uk-strip',
          data: { variant: 'standard', categoryIds: [c(0), c(1)] },
        },
        {
          type: 'banner',
          id: 'home-uk-banner',
          data: {
            variant: 'promo',
            image: 'https://img.example/home-banner.jpg',
            link: { kind: 'page', ref: 'browse-featured' },
            aspectRatio: '16:9',
          },
        },
        {
          type: 'featuredStore',
          id: 'home-uk-featured',
          data: { storeId: s(0), headline: 'Brand of the week' },
        },
        {
          type: 'textBlock',
          id: 'home-uk-text',
          data: {
            title: 'Why Cashblack',
            body: 'Shop Black-owned brands and earn cashback on every order.',
            align: 'left',
          },
        },
        { type: 'divider', id: 'home-uk-divider', data: { style: 'space' } },
        {
          type: 'pillCta',
          id: 'home-uk-pill',
          data: {
            label: 'Get the app',
            link: { kind: 'external', ref: 'https://cashblack.com/app' },
            tone: 'primary',
          },
        },
        // The showcase is PUBLISHED (v1 == the draft): published-and-clean (8b).
      ],
      true,
    ),
    // A us home page, also published (v1 == the draft).
    mkPage(
      'home',
      'us',
      [
        {
          type: 'hero',
          id: 'home-us-hero',
          data: {
            image: 'https://img.example/home-hero-us.jpg',
            title: 'Cashback on Black-owned brands',
          },
        },
        {
          type: 'storeGrid',
          id: 'home-us-grid',
          data: { storeIds: [s(3), s(4)], columns: 2 },
        },
        {
          type: 'pillCta',
          id: 'home-us-pill',
          data: {
            label: 'Refer a friend',
            link: { kind: 'page', ref: 'home' },
            tone: 'secondary',
          },
        },
      ],
      true,
    ),
    // browse-featured in both markets.
    mkPage('browse-featured', 'uk', [
      {
        type: 'carousel',
        id: 'bf-uk-carousel',
        data: { variant: 'store', title: 'Featured', storeIds: [s(2), s(5)] },
      },
      {
        type: 'categoryStrip',
        id: 'bf-uk-strip',
        data: { variant: 'row', categoryIds: [c(2), c(3)] },
      },
      {
        type: 'featuredStore',
        id: 'bf-uk-featured',
        data: { storeId: s(1) },
      },
    ]),
    mkPage('browse-featured', 'us', [
      {
        type: 'banner',
        id: 'bf-us-banner',
        data: {
          variant: 'standard',
          image: 'https://img.example/bf-us.jpg',
          link: { kind: 'category', ref: c(0) },
        },
      },
      {
        type: 'storeGrid',
        id: 'bf-us-grid',
        data: {
          title: 'Editor picks',
          storeIds: [s(6), s(7), s(8)],
          columns: 3,
        },
      },
    ]),
    // A category page and a seasonal page (the templated vocabulary families).
    mkPage(`category-${c(0)}`, 'uk', [
      {
        type: 'categoryStrip',
        id: 'cat-uk-strip',
        data: { variant: 'standard', categoryIds: [c(0)] },
      },
      {
        type: 'carousel',
        id: 'cat-uk-carousel',
        data: { variant: 'category', categoryIds: [c(0), c(4)] },
      },
      {
        type: 'textBlock',
        id: 'cat-uk-text',
        data: { body: 'The best Black-owned brands in this category.' },
      },
    ]),
    mkPage('seasonal-black-friday', 'us', [
      {
        type: 'hero',
        id: 'seasonal-us-hero',
        data: {
          image: 'https://img.example/bf-hero.jpg',
          title: 'Black Friday deals',
          cta: { label: 'Shop now', link: '/browse-featured' },
        },
      },
      {
        type: 'banner',
        id: 'seasonal-us-banner',
        data: {
          variant: 'promo',
          image: 'https://img.example/bf-banner.jpg',
          link: { kind: 'external', ref: 'https://cashblack.com/black-friday' },
        },
      },
      { type: 'divider', id: 'seasonal-us-divider', data: { style: 'thick' } },
      {
        type: 'pillCta',
        id: 'seasonal-us-pill',
        data: {
          label: 'See all deals',
          link: { kind: 'page', ref: 'browse-featured' },
          tone: 'subtle',
        },
      },
    ]),
  ];

  // Conventional rich-text CMS pages (D-cms-pages-core). The body is canonical TipTap
  // (ProseMirror) JSON. Fixed content (no draw from the main rng), so the main seed
  // stream stays byte-identical. A mix of markets (uk / us / all), kinds and statuses.
  const cmsBody = (heading: string, lead: string): CmsPage['body'] => ({
    type: 'doc',
    content: [
      {
        type: 'heading',
        attrs: { level: 2 },
        content: [{ type: 'text', text: heading }],
      },
      {
        type: 'paragraph',
        content: [
          { type: 'text', text: lead + ' ' },
          { type: 'text', marks: [{ type: 'bold' }], text: 'Cashblack' },
          {
            type: 'text',
            text: ' rewards you for shopping Black-owned brands.',
          },
        ],
      },
      {
        type: 'cta',
        attrs: { href: 'https://cashblack.com/join', tone: 'primary' },
        content: [{ type: 'text', text: 'Join now' }],
      },
    ],
  });
  let cmsPageId = 0;
  const mkCmsPage = (
    title: string,
    slug: string,
    kind: CmsPage['kind'],
    market: CmsPage['market'],
    status: CmsPage['status'],
    lead: string,
  ): CmsPage => ({
    id: ++cmsPageId,
    title,
    slug,
    kind,
    market,
    status,
    body: cmsBody(title, lead),
    publishedAt: status === 'published' ? daysAgo(20) : null,
    createdBy: 'nia.editor@cashblack.example',
    createdAt: daysAgo(120),
    updatedAt: daysAgo(20),
  });
  const cmsPages: CmsPage[] = [
    mkCmsPage('About us', 'about', 'marketing', 'all', 'published', 'About'),
    mkCmsPage(
      'Terms of service',
      'terms',
      'legal',
      'all',
      'published',
      'Read the terms.',
    ),
    mkCmsPage(
      'Privacy policy',
      'privacy',
      'legal',
      'all',
      'published',
      'Your privacy matters.',
    ),
    mkCmsPage('Help centre', 'help', 'help', 'uk', 'published', 'Need a hand?'),
    mkCmsPage(
      'Black Friday landing',
      'black-friday',
      'marketing',
      'us',
      'draft',
      'Coming soon.',
    ),
  ];

  // Conventional rich-text blog (D-cms-blog). REUSES the cmsPages body and lifecycle in a
  // SEPARATE collection plus a managed editorial-category vocabulary (cloned from
  // storeTags). Fixed content (no draw from the main rng), so the main seed stream stays
  // byte-identical. A mix of markets (uk / us / all), categories and statuses.
  const blogCategories: BlogCategory[] = [
    {
      id: 1,
      key: 'news',
      label: 'News',
      description: 'Company and partner announcements.',
    },
    {
      id: 2,
      key: 'guides',
      label: 'Guides',
      description: 'How to get the most from cashback.',
    },
    {
      id: 3,
      key: 'brand-spotlight',
      label: 'Brand Spotlight',
      description: 'A closer look at a Black-owned brand.',
    },
    {
      id: 4,
      key: 'how-to',
      label: 'How-to',
      description: 'Step-by-step walkthroughs.',
    },
  ];
  let blogPostId = 0;
  const mkBlogPost = (
    title: string,
    slug: string,
    market: BlogPost['market'],
    status: BlogPost['status'],
    categoryKey: string,
    author: string,
    lead: string,
  ): BlogPost => ({
    id: ++blogPostId,
    title,
    slug,
    market,
    status,
    categoryKey,
    featuredImageUrl: `https://img.cashblack.com/blog/${slug}.jpg`,
    author,
    excerpt: lead,
    body: cmsBody(title, lead),
    publishedAt: status === 'published' ? daysAgo(15) : null,
    createdBy: 'nia.editor@cashblack.example',
    createdAt: daysAgo(90),
    updatedAt: daysAgo(15),
  });
  const blogPosts: BlogPost[] = [
    mkBlogPost(
      'Cashblack launches in the UK',
      'cashblack-launches-uk',
      'uk',
      'published',
      'news',
      'The Cashblack team',
      'We are live.',
    ),
    mkBlogPost(
      'Brand spotlight on Liha Beauty',
      'spotlight-liha-beauty',
      'all',
      'published',
      'brand-spotlight',
      'Liha Beauty',
      'Meet the brand.',
    ),
    mkBlogPost(
      'Your guide to cashback',
      'guide-to-cashback',
      'all',
      'published',
      'guides',
      'Ada Editor',
      'Here is how it works.',
    ),
    mkBlogPost(
      'How to refer a friend',
      'how-to-refer-a-friend',
      'us',
      'draft',
      'how-to',
      'The Cashblack team',
      'Share and earn.',
    ),
  ];

  // Marketing-site testimonials (D-cms-testimonials-seo). A FLAT structured surface (no
  // rich text, no publish), the active / inactive toggle. Fixed content (no draw from the
  // main rng), so the main seed stream stays byte-identical. A mix of markets and statuses.
  let testimonialId = 0;
  const mkTestimonial = (
    quote: string,
    author: string,
    rating: number,
    market: Testimonial['market'],
    status: Testimonial['status'],
  ): Testimonial => ({
    id: ++testimonialId,
    quote,
    author,
    rating,
    market,
    status,
    createdBy: 'nia.editor@cashblack.example',
    createdAt: daysAgo(60),
    updatedAt: daysAgo(20),
  });
  const testimonials: Testimonial[] = [
    mkTestimonial(
      'Cashblack paid for my weekly shop and I get to back brands I believe in.',
      'Amara from London',
      5,
      'uk',
      'active',
    ),
    mkTestimonial(
      'The cashback adds up fast and the brands are brilliant.',
      'Jordan from Atlanta',
      4,
      'us',
      'active',
    ),
    mkTestimonial(
      'Earning while shopping the brands I already love is a win.',
      'The Cashblack community',
      5,
      'all',
      'active',
    ),
    mkTestimonial(
      'A lovely idea that needs a few more partner brands near me.',
      'Sam from Leeds',
      3,
      'uk',
      'inactive',
    ),
  ];

  // Per-page SEO meta rules (D-cms-testimonials-seo). Keyed by a FREE pageKey / path (no
  // FK), spanning the four page-identifier types, with per-market region variants. Fixed
  // content (no draw from the main rng), so the main seed stream stays byte-identical.
  let seoRuleId = 0;
  const mkSeoRule = (
    pageKey: string,
    metaTitle: string,
    metaDescription: string,
    market: SeoRule['market'],
  ): SeoRule => ({
    id: ++seoRuleId,
    pageKey,
    metaTitle,
    metaDescription,
    ogImageUrl: `https://img.cashblack.com/og/${seoRuleId}.jpg`,
    market,
    createdBy: 'nia.editor@cashblack.example',
    createdAt: daysAgo(60),
    updatedAt: daysAgo(20),
  });
  const seoRules: SeoRule[] = [
    mkSeoRule(
      'about',
      'About Cashblack',
      'Cashback for shopping Black-owned brands.',
      'all',
    ),
    mkSeoRule(
      'blog/cashblack-launches-uk',
      'Cashblack launches in the UK',
      'Read about our UK launch.',
      'uk',
    ),
    mkSeoRule('home', 'Cashblack UK', 'Earn cashback in the UK.', 'uk'),
    mkSeoRule('home', 'Cashblack US', 'Earn cashback in the US.', 'us'),
    mkSeoRule('stores', 'All stores', 'Browse every partner store.', 'all'),
  ];

  // Marketing-site slider slides (D-cms-sliders). Placement-grouped with a per-placement
  // order, the active toggle, NO publish. Fixed content (no draw from the main rng), so the
  // main seed stream stays byte-identical. Two placements, an 'all' slide sharing a
  // placement with single-market slides, an active and an inactive one.
  let slideId = 0;
  const mkSlide = (
    placement: string,
    order: number,
    market: SliderSlide['market'],
    active: boolean,
    label: string,
    link: string,
  ): SliderSlide => ({
    id: ++slideId,
    placement,
    imageUrl: `https://img.cashblack.com/slides/${slideId}.jpg`,
    cta: { label, link },
    order,
    market,
    active,
    createdBy: 'nia.editor@cashblack.example',
    createdAt: daysAgo(45),
    updatedAt: daysAgo(15),
  });
  const sliderSlides: SliderSlide[] = [
    mkSlide(
      'homepage-hero',
      0,
      'uk',
      true,
      'Shop Black-owned UK brands',
      'https://cashblack.com/uk',
    ),
    mkSlide(
      'homepage-hero',
      1,
      'all',
      true,
      'Earn cashback on every shop',
      'https://cashblack.com/how-it-works',
    ),
    mkSlide(
      'homepage-hero',
      2,
      'us',
      true,
      'Shop Black-owned US brands',
      'https://cashblack.com/us',
    ),
    mkSlide(
      'homepage-hero',
      3,
      'uk',
      false,
      'Last season promo',
      'https://cashblack.com/promo',
    ),
    mkSlide(
      'category-top',
      0,
      'all',
      true,
      'Beauty edit',
      'https://cashblack.com/beauty',
    ),
    mkSlide(
      'category-top',
      1,
      'all',
      true,
      'Food and drink edit',
      'https://cashblack.com/food',
    ),
  ];

  // Navigation menu items (D-cms-menu, the LAST CMS piece). An adjacency-list tree
  // (parentId + order) capped at 2 levels, grouped by a free location key, the active
  // toggle, NO publish. Fixed content (no draw from the main rng), so the main seed stream
  // stays byte-identical. A header with a 2-level tree (a heading with children) and a
  // footer, link kinds spanning internal and external, an 'all' item among single-market.
  let menuItemId = 0;
  const mkMenuItem = (
    location: string,
    parentId: number | null,
    order: number,
    label: string,
    market: MenuItem['market'],
    active: boolean,
    link?: MenuItem['link'],
  ): MenuItem => ({
    id: ++menuItemId,
    location,
    parentId,
    order,
    label,
    link,
    market,
    active,
    createdBy: 'nia.editor@cashblack.example',
    createdAt: daysAgo(60),
    updatedAt: daysAgo(15),
  });
  // Header: Home, Shop (a heading with children), About (all), Help.
  const mHome = mkMenuItem('header', null, 0, 'Home', 'all', true, {
    kind: 'page',
    ref: 'home',
  });
  const mShop = mkMenuItem('header', null, 1, 'Shop', 'all', true);
  const mAbout = mkMenuItem('header', null, 2, 'About', 'all', true, {
    kind: 'page',
    ref: 'about',
  });
  const mHelp = mkMenuItem('header', null, 3, 'Help', 'uk', true, {
    kind: 'page',
    ref: 'help',
  });
  const menuItems: MenuItem[] = [
    mHome,
    mShop,
    mAbout,
    mHelp,
    // Shop's children.
    mkMenuItem('header', mShop.id, 0, 'Beauty', 'all', true, {
      kind: 'category',
      ref: 'beauty',
    }),
    mkMenuItem('header', mShop.id, 1, 'Food and drink', 'all', true, {
      kind: 'category',
      ref: 'food',
    }),
    mkMenuItem('header', mShop.id, 2, 'Partner brands', 'all', true, {
      kind: 'external',
      ref: 'https://partners.cashblack.com',
    }),
    // Footer.
    mkMenuItem('footer', null, 0, 'Terms', 'all', true, {
      kind: 'page',
      ref: 'terms',
    }),
    mkMenuItem('footer', null, 1, 'Privacy', 'all', true, {
      kind: 'page',
      ref: 'privacy',
    }),
    mkMenuItem('footer', null, 2, 'Contact', 'all', true, {
      kind: 'external',
      ref: 'https://cashblack.com/contact',
    }),
  ];

  // Brick-and-mortar / card-linked-offer venues (D-offline-stores). Its own collection, one
  // row per physical VENUE, the offline OB / CLO tracking axis. Fixed content (no draw from
  // the main rng), so the main seed stream stays byte-identical. A linked and standalone
  // venue, both tracking rails, a chain, an IRL event and an inactive one, the uk / us spread.
  let offlineStoreId = 0;
  const linkedBrandId = merchants[0]?.id ?? 1;
  const mkOfflineStore = (
    name: string,
    merchantId: number | null,
    addressLine: string,
    postcode: string,
    country: string,
    market: OfflineStore['market'],
    trackingMethod: OfflineStore['trackingMethod'],
    cardLinkConfig: OfflineStore['cardLinkConfig'],
    irlEvent: boolean,
    status: OfflineStore['status'],
  ): OfflineStore => ({
    id: ++offlineStoreId,
    name,
    merchantId,
    addressLine,
    postcode,
    country,
    market,
    trackingMethod,
    cardLinkConfig,
    irlEvent,
    status,
    createdBy: 'nia.editor@cashblack.example',
    createdAt: daysAgo(75),
    updatedAt: daysAgo(20),
  });
  const offlineStores: OfflineStore[] = [
    mkOfflineStore(
      'Liha Beauty Store',
      linkedBrandId,
      '12 Brixton Road',
      'SW9 8PR',
      'United Kingdom',
      'uk',
      'clo',
      { mcc: '5977', mid: 'MID-LIHA-01', isChain: false },
      false,
      'active',
    ),
    mkOfflineStore(
      'Ace Caribbean Kitchen',
      null,
      '88 Dalston Lane',
      'E8 3RL',
      'United Kingdom',
      'uk',
      'ob',
      { mcc: '5812', isChain: false },
      false,
      'active',
    ),
    mkOfflineStore(
      'Afro Market Pop-up',
      null,
      'Mayfield Depot',
      'M1 2PY',
      'United Kingdom',
      'uk',
      'clo',
      { mcc: '5999', isChain: false },
      true,
      'active',
    ),
    mkOfflineStore(
      'Harlem Soul Diner',
      null,
      '215 Lenox Avenue',
      '10027',
      'United States',
      'us',
      'clo',
      { mcc: '5812', mid: 'MID-US-CHAIN', isChain: true },
      false,
      'inactive',
    ),
    mkOfflineStore(
      'Brooklyn Roots Cafe',
      null,
      '500 Fulton Street',
      '11201',
      'United States',
      'us',
      'ob',
      { mcc: '5814', isChain: false },
      false,
      'active',
    ),
  ];

  // System settings + feature flags + the Plus marketing flag (D-settings-s1, Phase 9). The
  // FIRST singleton-config surface, ONE ROW PER MARKET (the per-market-row precedent). Fixed
  // content (no draw from the main rng), so the main seed stream stays byte-identical. The
  // trust-tier spread is inherent: a low-stakes setting (site name, automatic audit), a
  // cashback default (settings:configure, forced reason), feature flags (feature_flags:write,
  // forced reason) and the Plus marketing flag (marketing_flags:write, super_admin + forced
  // reason), across the uk / us markets. The live apps reading the config are the migration seam.
  // Fixed ids (one row per market); no counter and no rng draw, so the main seed stream
  // stays byte-identical.
  const systemSettings: SystemSetting[] = [
    {
      id: 1,
      market: 'uk',
      siteName: 'Cashblack',
      currencyDisplay: 'gbp',
      cashbackDefault: 3,
      cashbackChangeReason: null,
      updatedBy: 'nia.editor@cashblack.example',
      updatedAt: daysAgo(30),
    },
    {
      id: 2,
      market: 'us',
      siteName: 'Cashblack US',
      currencyDisplay: 'usd',
      cashbackDefault: 4,
      cashbackChangeReason: null,
      updatedBy: 'nia.editor@cashblack.example',
      updatedAt: daysAgo(30),
    },
  ];
  const featureFlags: FeatureFlag[] = [
    {
      id: 1,
      key: 'new-dashboard',
      label: 'New dashboard',
      description: 'The redesigned member dashboard.',
      market: 'uk',
      enabled: true,
      lastChangeReason: 'Rolled out to UK after the closed beta.',
      updatedBy: 'nia.editor@cashblack.example',
      updatedAt: daysAgo(14),
    },
    {
      id: 2,
      key: 'new-dashboard',
      label: 'New dashboard',
      description: 'The redesigned member dashboard.',
      market: 'us',
      enabled: false,
      lastChangeReason: null,
      updatedBy: 'nia.editor@cashblack.example',
      updatedAt: daysAgo(30),
    },
    {
      id: 3,
      key: 'offline-stores-map',
      label: 'Offline stores map',
      description: 'The in-app map of brick-and-mortar venues.',
      market: 'uk',
      enabled: false,
      lastChangeReason: null,
      updatedBy: 'nia.editor@cashblack.example',
      updatedAt: daysAgo(20),
    },
  ];
  const plusMarketingFlags: PlusMarketingFlag[] = [
    {
      id: 1,
      market: 'uk',
      state: 'dormant',
      lastChangeReason: null,
      updatedBy: 'nia.editor@cashblack.example',
      updatedAt: daysAgo(30),
    },
    {
      id: 2,
      market: 'us',
      state: 'partial',
      lastChangeReason: 'Partial launch for the US beta cohort.',
      updatedBy: 'nia.editor@cashblack.example',
      updatedAt: daysAgo(10),
    },
  ];

  // Transactional email templates (D-settings-s3-email-templates, Phase 9). A FIXED set,
  // ONE ROW PER (templateKey, market). Fixed content (no draw from the main rng), so the
  // main seed stream stays byte-identical. Each body uses only its template type's declared
  // variables (the const contract in emailTemplatesModel), so the clean-subset case exists.
  // The uk and us rows start from the same copy; the admin customises per market.
  const EMAIL_TEMPLATE_CONTENT: {
    key: string;
    subject: string;
    body: string;
  }[] = [
    {
      key: 'welcome',
      subject: 'Welcome to Cashblack, {{firstName}}',
      body: 'Hi {{firstName}},\n\nWelcome to Cashblack. Start shopping with your favourite brands and earn real cashback.\n\nThe Cashblack team',
    },
    {
      key: 'verify-email',
      subject: 'Confirm your email, {{firstName}}',
      body: 'Hi {{firstName}},\n\nPlease confirm your email by visiting {{verifyLink}}.\n\nThe Cashblack team',
    },
    {
      key: 'password-reset',
      subject: 'Reset your Cashblack password',
      body: 'Hi {{firstName}},\n\nReset your password here: {{resetLink}}. If you did not request this, ignore this email.\n\nThe Cashblack team',
    },
    {
      key: 'mobile-otp',
      subject: 'Your Cashblack code',
      body: 'Hi {{firstName}},\n\nYour one-time code is {{otpCode}}. It expires in ten minutes.\n\nThe Cashblack team',
    },
    {
      key: 'cashback-added',
      subject: 'You earned cashback at {{storeName}}',
      body: 'Hi {{firstName}},\n\n{{cashbackAmount}} of cashback from {{storeName}} has been added to your account.\n\nThe Cashblack team',
    },
    {
      key: 'cashout-requested',
      subject: 'We received your cashout request',
      body: 'Hi {{firstName}},\n\nWe have received your request to cash out {{amount}}. We will let you know when it is paid.\n\nThe Cashblack team',
    },
    {
      key: 'cashout-paid',
      subject: 'Your cashout has been paid',
      body: 'Hi {{firstName}},\n\nWe have paid {{amount}} to your {{payoutMethod}}.\n\nThe Cashblack team',
    },
    {
      key: 'referral-earned',
      subject: 'Your friend {{friendName}} earned you a reward',
      body: 'Hi {{firstName}},\n\n{{friendName}} joined Cashblack, so you have earned {{referralAmount}}.\n\nThe Cashblack team',
    },
    {
      key: 'plus-activated',
      subject: 'Your Cashblack Plus is active',
      body: 'Hi {{firstName}},\n\nYour Cashblack Plus membership is now active. Enjoy your boosted rates.\n\nThe Cashblack team',
    },
    {
      key: 'donation-thanks',
      subject: 'Thank you for supporting {{charityName}}',
      body: 'Hi {{firstName}},\n\nThank you for donating {{amount}} to {{charityName}} through Cashblack.\n\nThe Cashblack team',
    },
  ];
  const emailTemplates: EmailTemplate[] = EMAIL_TEMPLATE_CONTENT.flatMap(
    (t, i) =>
      (['uk', 'us'] as const).map((market, j) => ({
        id: i * 2 + j + 1,
        templateKey: t.key,
        market,
        subject: t.subject,
        body: t.body,
        updatedBy: 'nia.editor@cashblack.example',
        updatedAt: daysAgo(20),
      })),
  );

  // US compliance settings (D-settings-s4-us-compliance, Phase 9 S4): the last and
  // highest-consequence Settings surface. FIXED content (no PRNG draw), so the main seed
  // stream stays byte-identical. All US-only (market 'us').
  //
  // The 1099-K thresholds MIRROR THRESHOLD_BY_YEAR (the seed default and code fallback in
  // taxReportModel): promoting them to config makes these rows the source of truth the tax
  // report reads, with the constant demoted to the fallback. Keep the figures in sync.
  const complianceThresholds: ComplianceThreshold[] = [
    { year: 2023, thresholdUsd: 20000 },
    { year: 2024, thresholdUsd: 5000 },
    { year: 2025, thresholdUsd: 2500 },
    { year: 2026, thresholdUsd: 600 },
  ].map((t, i) => ({
    id: i + 1,
    market: 'us',
    year: t.year,
    thresholdUsd: t.thresholdUsd,
    lastChangeReason: null,
    updatedBy: 'compliance@cashblack.example',
    updatedAt: daysAgo(20),
  }));

  // The state privacy-regime toggles: a CONTENT-OWNED starting set (states with live
  // comprehensive privacy laws), NOT an authoritative legal list. Some on, some not, so the
  // toggle has both directions to exercise. New states are added as rows as their laws come
  // live. The real set is the compliance team's to own.
  const complianceStateToggles: ComplianceStateToggle[] = [
    { stateCode: 'CA', lawName: 'CCPA / CPRA', enabled: true },
    { stateCode: 'VA', lawName: 'VCDPA', enabled: true },
    { stateCode: 'CO', lawName: 'CPA', enabled: true },
    { stateCode: 'CT', lawName: 'CTDPA', enabled: false },
    { stateCode: 'UT', lawName: 'UCPA', enabled: false },
  ].map((s, i) => ({
    id: i + 1,
    market: 'us',
    stateCode: s.stateCode,
    lawName: s.lawName,
    enabled: s.enabled,
    lastChangeReason: null,
    updatedBy: 'compliance@cashblack.example',
    updatedAt: daysAgo(20),
  }));

  // The privacy / terms REFERENCES: slug pointers into the CMS legal pages (kind 'legal').
  // The seeded cmsPages carry 'privacy' and 'terms' (market 'all', which includes US), so
  // these resolve cleanly. NOT a document store; the CMS owns the content.
  const complianceLegalReferences: ComplianceLegalReference[] = [
    { referenceKey: 'privacy', cmsSlug: 'privacy' },
    { referenceKey: 'terms', cmsSlug: 'terms' },
  ].map((r, i) => ({
    id: i + 1,
    market: 'us',
    referenceKey: r.referenceKey,
    cmsSlug: r.cmsSlug,
    lastChangeReason: null,
    updatedBy: 'compliance@cashblack.example',
    updatedAt: daysAgo(20),
  }));

  // Events (D-events-explorer, Phase 9): a read-only debug / search corpus mirroring the
  // backbone events table. FIXED content (no draw from the main rng) so the main seed stream
  // stays byte-identical. A REPRESENTATIVE spread across the LOCKED 25-value event_type
  // catalog, across a few members in both markets and a time range, with a recurring
  // store_id (777) so the property-substring search has a target. The market is DERIVED from
  // the event's user and MATERIALIZED here (the backbone has no market column). The real app
  // and backend event stream is the migration seam; the admin reads events, never emits them.
  const eventUsers = [
    ...users.filter((u) => u.market === 'uk').slice(0, 2),
    ...users.filter((u) => u.market === 'us').slice(0, 2),
  ];
  const EVENT_SPECS: {
    type: EventType;
    properties: Record<string, unknown>;
  }[] = [
    { type: 'app_open', properties: {} },
    { type: 'screen_view', properties: { screen_name: 'home' } },
    { type: 'tab_switch', properties: { from_tab: 'home', to_tab: 'stores' } },
    {
      type: 'error_shown',
      properties: { error_type: 'network', screen: 'checkout' },
    },
    {
      type: 'block_view',
      properties: { block_type: 'hero', block_id: 12, page_id: 'home' },
    },
    {
      type: 'block_interact',
      properties: {
        block_type: 'hero',
        block_id: 12,
        page_id: 'home',
        interaction_kind: 'tap',
      },
    },
    { type: 'signup', properties: { method: 'email' } },
    { type: 'first_link_click', properties: { store_id: 777 } },
    { type: 'first_qualifying_transaction', properties: { amount: 4200 } },
    { type: 'first_payout', properties: { amount: 2500 } },
    { type: 'welcome_stack_n', properties: { stack_position: 2 } },
    { type: 'store_view', properties: { store_id: 777, channel_id: 3 } },
    {
      type: 'cashback_offer_revealed',
      properties: { store_id: 777, rate: 5 },
    },
    { type: 'upgrade_prompt_shown', properties: { location: 'wallet' } },
    { type: 'upgrade_prompt_tapped', properties: { location: 'wallet' } },
    { type: 'plus_upgrade', properties: { plan: 'annual' } },
    { type: 'plus_downgrade', properties: { reason: 'cost' } },
    { type: 'payment_failed', properties: { reason: 'card_declined' } },
    {
      type: 'manual_attribution_pending',
      properties: { transaction_id: 5001 },
    },
    {
      type: 'manual_attribution_resolved',
      properties: { transaction_id: 5001, outcome: 'credited' },
    },
    { type: 'dsar_filed', properties: { request_id: 9001 } },
    { type: 'fraud_held', properties: { risk_score: 82 } },
    { type: 'search_input', properties: { query: 'nike' } },
    {
      type: 'search_result_tap',
      properties: { query: 'nike', result_id: 777 },
    },
    { type: 'search_no_results', properties: { query: 'zzzzz' } },
    { type: 'store_view', properties: { store_id: 777, channel_id: 5 } },
  ];
  const events: Event[] = EVENT_SPECS.map((spec, i) => {
    const user = eventUsers[i % eventUsers.length];
    return {
      id: i + 1,
      userId: user.id,
      eventType: spec.type,
      occurredAt: new Date(BASE_MS - i * DAY).toISOString(),
      properties: spec.properties,
      market: user.market,
    };
  });

  // Internal alerts (D-internal-alerts, Phase 9, the last feature surface): the CONFIG rows +
  // the global channel config + a representative FIRED-ALERT trail. FIXED content (no draw from
  // the main rng) so the main seed stream stays byte-identical. The backend firing engine reads
  // the config and writes the trail; the mock seeds both. The admin configures and views, it
  // never fires or delivers (the migration seam).
  const alertMeta = {
    lastChangeReason: null,
    updatedBy: 'ops@cashblack.example',
    updatedAt: daysAgo(20),
  };
  const alertConfigs: AlertConfig[] = [
    // Monetary types: REGION-KEYED (one row per market, the currency differs).
    {
      id: 1,
      alertType: 'high_value_cashout',
      market: 'uk',
      enabled: true,
      slackEnabled: true,
      emailEnabled: true,
      thresholdAmount: 500,
      thresholdCount: null,
      windowMinutes: null,
      ...alertMeta,
    },
    {
      id: 2,
      alertType: 'high_value_cashout',
      market: 'us',
      enabled: true,
      slackEnabled: true,
      emailEnabled: true,
      thresholdAmount: 500,
      thresholdCount: null,
      windowMinutes: null,
      ...alertMeta,
    },
    {
      id: 3,
      alertType: 'large_refund',
      market: 'uk',
      enabled: true,
      slackEnabled: true,
      emailEnabled: false,
      thresholdAmount: 200,
      thresholdCount: null,
      windowMinutes: null,
      ...alertMeta,
    },
    {
      id: 4,
      alertType: 'large_refund',
      market: 'us',
      enabled: true,
      slackEnabled: true,
      emailEnabled: false,
      thresholdAmount: 200,
      thresholdCount: null,
      windowMinutes: null,
      ...alertMeta,
    },
    // Count type: GLOBAL (a count in a window, not currency-dependent).
    {
      id: 5,
      alertType: 'suspicious_activity',
      market: 'global',
      enabled: true,
      slackEnabled: true,
      emailEnabled: true,
      thresholdAmount: null,
      thresholdCount: 10,
      windowMinutes: 60,
      ...alertMeta,
    },
    // No-threshold type: GLOBAL (fires on any error).
    {
      id: 6,
      alertType: 'system_error',
      market: 'global',
      enabled: true,
      slackEnabled: true,
      emailEnabled: false,
      thresholdAmount: null,
      thresholdCount: null,
      windowMinutes: null,
      ...alertMeta,
    },
  ];
  const alertChannelConfigs: AlertChannelConfig[] = [
    {
      id: 1,
      slackWebhookUrl:
        'https://hooks.slack.com/services/SEED/PLACEHOLDER/webhook',
      emailRecipients: ['ops@cashblack.example', 'oncall@cashblack.example'],
      emailFrequency: 'daily',
      lastChangeReason: null,
      updatedBy: 'ops@cashblack.example',
      updatedAt: daysAgo(20),
    },
  ];
  // The fired-alert trail: representative instances across the types, both markets and a time
  // range, each with a trigger context bag. Seeded (the backend is the real writer).
  const FIRED_SPECS: {
    alertType: FiredAlert['alertType'];
    market: FiredAlert['market'];
    trigger: Record<string, unknown>;
    channelsDelivered: string[];
  }[] = [
    {
      alertType: 'high_value_cashout',
      market: 'uk',
      trigger: { cashoutId: 4821, amount: 750 },
      channelsDelivered: ['slack', 'email'],
    },
    {
      alertType: 'high_value_cashout',
      market: 'us',
      trigger: { cashoutId: 4822, amount: 900 },
      channelsDelivered: ['slack', 'email'],
    },
    {
      alertType: 'large_refund',
      market: 'uk',
      trigger: { refundId: 331, amount: 260 },
      channelsDelivered: ['slack'],
    },
    {
      alertType: 'large_refund',
      market: 'us',
      trigger: { refundId: 332, amount: 410 },
      channelsDelivered: ['slack'],
    },
    {
      alertType: 'suspicious_activity',
      market: 'global',
      trigger: { eventCount: 14, windowMinutes: 60, userId: 8123 },
      channelsDelivered: ['slack', 'email'],
    },
    {
      alertType: 'system_error',
      market: 'global',
      trigger: {
        eventSummary: 'payout worker timeout',
        service: 'payout-engine',
      },
      channelsDelivered: ['slack'],
    },
    {
      alertType: 'high_value_cashout',
      market: 'us',
      trigger: { cashoutId: 4830, amount: 1200 },
      channelsDelivered: ['slack', 'email'],
    },
    {
      alertType: 'large_refund',
      market: 'uk',
      trigger: { refundId: 340, amount: 305 },
      channelsDelivered: ['slack'],
    },
  ];
  const firedAlerts: FiredAlert[] = FIRED_SPECS.map((spec, i) => ({
    id: i + 1,
    alertType: spec.alertType,
    firedAt: new Date(BASE_MS - i * DAY).toISOString(),
    trigger: spec.trigger,
    channelsDelivered: spec.channelsDelivered,
    market: spec.market,
  }));

  return {
    merchants,
    merchantRegions,
    storeChannels,
    storeTags,
    merchantRegionTags,
    rateRules,
    pageSchemas,
    cmsPages,
    blogPosts,
    blogCategories,
    testimonials,
    seoRules,
    sliderSlides,
    menuItems,
    offlineStores,
    systemSettings,
    featureFlags,
    plusMarketingFlags,
    emailTemplates,
    complianceThresholds,
    complianceStateToggles,
    complianceLegalReferences,
    events,
    alertConfigs,
    alertChannelConfigs,
    firedAlerts,
    affiliateNetworks,
    affiliateNetworkMarkets,
    users,
    transactions,
    cashoutRequests,
    balanceLedger,
    tickets,
    cannedResponses,
    impersonationSessions,
    dsarRequests,
    consents,
    sanctionsMatches,
    erasureRequests,
    welcomeBonusFundings,
    charities,
    charityTypes,
    categories,
    clicks,
    promoCodes,
    placements,
    auditLog: SEED_AUDIT_LOG.map((e) => ({ ...e })),
  };
}

// ~30% of regions have no tags; the rest carry 1-3 distinct tag keys.
function pickTagKeys(rng: Rng, storeTags: readonly StoreTag[]): string[] {
  if (chance(rng, 0.3)) return [];
  const count = randInt(rng, 1, 3);
  const chosen = new Set<string>();
  while (chosen.size < count) chosen.add(pick(rng, storeTags).key);
  return [...chosen];
}

// 1-2 distinct founder names drawn from the name pools (D-ownership).
function makeFounderNames(rng: Rng): string[] {
  const count = randInt(rng, 1, 2);
  const chosen = new Set<string>();
  while (chosen.size < count) {
    chosen.add(`${pick(rng, FIRST_NAMES)} ${pick(rng, LAST_NAMES)}`);
  }
  return [...chosen];
}
